// @ts-nocheck
import './App.css';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import Calendar from './Calendar';
import Login, { ResetPasswordPage } from './login';
import {
    buildGradientCss,
    getContrastTextColor,
    hexToRgbString,
    normalizeHexColor,
    resolveEffectiveBackground,
    toCssBackgroundImage,
} from './themeUtils';
import {
    ACCENT_SWATCHES,
    cloneThemePack,
    DEFAULT_THEME,
    EMPTY_CUSTOM_THEME,
    FEATURED_THEMES,
    inferThemeBackgroundMode,
    PRESET_THEMES,
    sanitizeThemePack,
} from './themeLibrary';
import {
    buildThemePackId,
    buildThemeShareTargets,
    clearSharedThemeValueFromLocation,
    extractSharedThemeValue,
    mergeThemePacks,
    PENDING_SHARED_THEME_STORAGE_KEY,
    readSharedThemeValueFromLocation,
} from './themeSharing';
import { WEATHER_SLOTS } from './weatherScenes';
import { requestWeatherLocation } from './weatherLocation.js';

import leftOpenIcon from './icons/panel-left-open.svg';
import leftCloseIcon from './icons/panel-left-close.svg';
import rightOpenIcon from './icons/panel-right-open.svg';
import rightCloseIcon from './icons/panel-right-close.svg';
import shareIcon from './icons/share-theme.svg';

const RAW_API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5000';
const API_ROOT = RAW_API_BASE.endsWith('/api') ? RAW_API_BASE : `${RAW_API_BASE}/api`;

function ShareArrowIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 5h5v5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 14 19 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19 13v4a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function CopyIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="9" y="9" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M7 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
}

function CodeHashIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 4 7 20M17 4l-2 16M4 9h16M3 15h16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
}

function MessagesIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 7h12M6 11h9M7 18l-3 2V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H7Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function MailIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="m5 7 7 6 7-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function FacebookIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 8h3V4h-3c-2.2 0-4 1.8-4 4v3H7v4h3v5h4v-5h3l1-4h-4V8Z" fill="currentColor" />
        </svg>
    );
}

function WhatsAppIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 4a8 8 0 0 0-6.9 12l-1.1 4 4.1-1A8 8 0 1 0 12 4Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9.7 9.5c.2-.4.4-.4.6-.4h.5c.1 0 .3.1.3.3l.8 1.9c.1.2 0 .4-.1.5l-.4.5c.7 1.3 1.8 2.3 3.1 3l.5-.4c.2-.1.4-.1.5-.1l1.9.8c.2.1.3.2.3.3v.5c0 .3-.1.5-.4.6-.4.2-1 .3-1.6.2-2-.4-5.1-3.3-5.6-5.4-.1-.6 0-1.2.2-1.7Z" fill="currentColor" />
        </svg>
    );
}

// ── Theme system ──────────────────────────────────────────────────────────────
const LEGACY_THEME_STORAGE_KEY = 'calpp_theme';
const THEME_STORAGE_KEY = 'calpp_theme';
const SAVED_THEME_PACKS_STORAGE_KEY = 'calpp_theme_packs';

const BUILT_IN_THEME_IDS = new Set([
    ...PRESET_THEMES.filter((theme) => theme.id !== 'custom').map((theme) => theme.id),
    ...FEATURED_THEMES.map((theme) => theme.id),
]);

function loadTheme() {
    try {
        const raw = localStorage.getItem(THEME_STORAGE_KEY) || localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
        if (!raw) return null;
        return sanitizeThemePack(JSON.parse(raw), DEFAULT_THEME);
    } catch {
        return null;
    }
}

function loadSavedThemePacks() {
    try {
        const raw = localStorage.getItem(SAVED_THEME_PACKS_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map((pack) => sanitizeThemePack(pack, EMPTY_CUSTOM_THEME)) : [];
    } catch {
        return [];
    }
}

function persistTheme(theme) {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
    } catch {
        // ignore quota errors
    }
}

function persistSavedThemePacks(packs) {
    try {
        localStorage.setItem(SAVED_THEME_PACKS_STORAGE_KEY, JSON.stringify(packs));
    } catch {
        // ignore quota errors
    }
}

function applyBtnColorOverride(color) {
    const theme = typeof color === 'object' && color !== null ? color : { btnColor: color };
    const resolvedColor = normalizeHexColor(theme?.btnColor || '#60a5fa');
    const btnTextColor = String(theme?.btnTextColor || '').trim()
        ? normalizeHexColor(theme.btnTextColor, getContrastTextColor(resolvedColor))
        : getContrastTextColor(resolvedColor);
    const gradientColors = Array.isArray(theme?.btnGradient?.colors)
        ? theme.btnGradient.colors.map((entry) => normalizeHexColor(entry, '')).filter(Boolean)
        : [];
    const buttonBackground = gradientColors.length >= 2
        ? `linear-gradient(${Number.isFinite(Number(theme?.btnGradient?.angle)) ? Number(theme.btnGradient.angle) : 135}deg, ${gradientColors.slice(0, 3).join(', ')})`
        : resolvedColor;

    document.documentElement.style.setProperty('--btn-color', resolvedColor);
    document.documentElement.style.setProperty('--btn-color-rgb', hexToRgbString(resolvedColor));
    document.documentElement.style.setProperty('--btn-text-color', btnTextColor);
    document.documentElement.style.setProperty('--btn-background', buttonBackground);
}

function resolveThemeButtonStyle(theme) {
    const baseColor = normalizeHexColor(theme?.btnColor || '#60a5fa');
    const btnTextColor = String(theme?.btnTextColor || '').trim()
        ? normalizeHexColor(theme.btnTextColor, getContrastTextColor(baseColor))
        : getContrastTextColor(baseColor);
    const gradientColors = Array.isArray(theme?.btnGradient?.colors)
        ? theme.btnGradient.colors.map((color) => normalizeHexColor(color, '')).filter(Boolean)
        : [];
    const hasGradient = gradientColors.length >= 2;
    const background = hasGradient
        ? `linear-gradient(${Number.isFinite(Number(theme?.btnGradient?.angle)) ? Number(theme.btnGradient.angle) : 135}deg, ${gradientColors.slice(0, 3).join(', ')})`
        : baseColor;

    return {
        '--btn-color': baseColor,
        '--btn-color-rgb': hexToRgbString(baseColor),
        '--btn-text-color': btnTextColor,
        background,
        color: btnTextColor,
    };
}

function inferThemePreview(theme) {
    if (theme?.coverPhoto) {
        return toCssBackgroundImage(theme.coverPhoto);
    }

    return toCssBackgroundImage(resolveEffectiveBackground(theme, null)) || theme?.preview || buildGradientCss(theme?.gradient);
}

function inferImageFit(theme) {
    return theme?.imageFit === 'contain' ? 'contain' : theme?.imageFit === 'center' ? 'auto' : 'cover';
}

function getEditableGradientStops(gradient) {
    const stops = Array.isArray(gradient?.stops) ? gradient.stops : [];
    if (stops.length >= 1) {
        return stops
            .map((stop, index) => ({
                color: normalizeHexColor(stop?.color || '#60a5fa'),
                position: Math.max(0, Math.min(100, Math.round(Number(stop?.position ?? (index === 0 ? 0 : 100))))),
            }))
            .sort((first, second) => first.position - second.position);
    }

    const colors = Array.isArray(gradient?.colors) ? gradient.colors : ['#2563eb'];
    return colors.slice(0, 5).map((color, index, palette) => ({
        color: normalizeHexColor(color),
        position: palette.length === 1 ? 0 : Math.round((index / (palette.length - 1)) * 100),
    }));
}

function syncGradientStops(nextStops, currentGradient = {}) {
    const orderedStops = nextStops
        .map((stop) => ({
            color: normalizeHexColor(stop?.color || '#60a5fa'),
            position: Math.max(0, Math.min(100, Math.round(Number(stop?.position ?? 0)))),
        }))
        .sort((first, second) => first.position - second.position);

    return {
        ...currentGradient,
        type: String(currentGradient?.type || 'linear').trim().toLowerCase() === 'radial' ? 'radial' : 'linear',
        angle: Number.isFinite(Number(currentGradient?.angle)) ? Number(currentGradient.angle) : 135,
        colors: orderedStops.map((stop) => stop.color),
        stops: orderedStops,
    };
}

const DEFAULT_GRADIENT_POSITIONS = [0, 50, 100, 25, 75, 12, 37, 62, 87, 6, 18, 31, 43, 56, 68, 81, 93];

function nextGradientPosition(stops = []) {
    const taken = new Set(stops.map((stop) => Math.round(stop.position)));
    for (const candidate of DEFAULT_GRADIENT_POSITIONS) {
        if (!taken.has(candidate)) {
            return candidate;
        }
    }

    return Math.min(100, Math.max(0, (stops[stops.length - 1]?.position ?? 0) + 8));
}

function isEditableThemePack(theme) {
    if (!theme) {
        return false;
    }

    return theme.id === 'custom' || !BUILT_IN_THEME_IDS.has(theme.id);
}

function serializeThemeDraft(theme) {
    return JSON.stringify(sanitizeThemePack(theme, EMPTY_CUSTOM_THEME));
}
// ─────────────────────────────────────────────────────────────────────────────

const REMINDER_OPTIONS = [
    { value: 0, label: 'At time of event' },
    { value: 5, label: '5 minutes before' },
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 1440, label: '1 day before' },
];
const SUPPORTED_AVATAR_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/heic',
    'image/heif',
    'image/bmp',
    'image/tiff',
]);
const AVATAR_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/avif,image/heic,image/heif,image/bmp,image/tiff';
const THEME_IMAGE_ACCEPT = AVATAR_ACCEPT;
const MAX_AVATAR_FILE_BYTES = 2 * 1024 * 1024;
const MAX_THEME_IMAGE_FILE_BYTES = 6 * 1024 * 1024;
const AVATAR_FORMAT_LABEL = 'PNG, JPEG, GIF, WEBP, AVIF, HEIC, HEIF, BMP, or TIFF';

export function decodeToken(token) {
    if (!token) {
        return null;
    }

    try {
        const payload = token.split('.')[1];
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(window.atob(normalized));
    } catch {
        return null;
    }
}

export function suggestionKey(suggestion) {
    return `${suggestion.title}|${suggestion.suggestedTime}|${suggestion.description}`;
}

export function dateWithSuggestedTime(base, suggestedTime) {
    const parts = suggestedTime.split(':');
    if (parts.length !== 2) {
        return new Date(base.getFullYear(), base.getMonth(), base.getDate(), 12, 0, 0, 0);
    }

    const hour = Number.parseInt(parts[0], 10);
    const minute = Number.parseInt(parts[1], 10);
    return new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate(),
        Number.isNaN(hour) ? 12 : hour,
        Number.isNaN(minute) ? 0 : minute,
        0,
        0,
    );
}

export function extractJsonArray(text) {
    const fenceMatch = /```(?:json)?\s*([\s\S]*?)```/m.exec(text);
    const fenced = fenceMatch?.[1]?.trim();
    if (fenced && fenced.startsWith('[') && fenced.endsWith(']')) {
        return fenced;
    }

    const start = text.indexOf('[');
    const end = text.lastIndexOf(']');
    if (start >= 0 && end > start) {
        return text.slice(start, end + 1).trim();
    }

    return '';
}

export function normalizeSuggestions(rawSuggestions) {
    const items = Array.isArray(rawSuggestions) ? rawSuggestions : [];
    if (
        items.length === 1 &&
        items[0]?.title === 'Parse error' &&
        typeof items[0]?.description === 'string'
    ) {
        const cleaned = extractJsonArray(items[0].description);
        if (cleaned) {
            try {
                const decoded = JSON.parse(cleaned);
                return Array.isArray(decoded) ? decoded : [];
            } catch {
                return items;
            }
        }
    }

    return items;
}

export function displayAssistantStatus(status) {
    const trimmed = String(status || '').trim();
    if (!trimmed) {
        return '';
    }

    return (trimmed.endsWith('...') || /[.!?]$/.test(trimmed)) ? trimmed : `${trimmed}...`;
}

export function waitForNextPaint() {
    return new Promise((resolve) => {
        const schedule =
            typeof window.requestAnimationFrame === 'function'
                ? window.requestAnimationFrame.bind(window)
                : (callback) => window.setTimeout(callback, 16);
        schedule(() => resolve());
    });
}

export function validateAvatarFile(file) {
    if (!file) {
        throw new Error('Choose an image file to upload.');
    }

    if (!SUPPORTED_AVATAR_TYPES.has(String(file.type || '').toLowerCase())) {
        throw new Error(`Profile picture must be ${AVATAR_FORMAT_LABEL}.`);
    }

    if (file.size > MAX_AVATAR_FILE_BYTES) {
        throw new Error('Profile picture must be 2 MB or smaller.');
    }
}

export function validateThemeImageFile(file, label = 'Image') {
    if (!file) {
        throw new Error(`Choose a ${label.toLowerCase()} to upload.`);
    }

    if (!SUPPORTED_AVATAR_TYPES.has(String(file.type || '').toLowerCase())) {
        throw new Error(`${label} must be ${AVATAR_FORMAT_LABEL}.`);
    }

    if (file.size > MAX_THEME_IMAGE_FILE_BYTES) {
        throw new Error(`${label} must be 6 MB or smaller.`);
    }
}

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string' && reader.result) {
                resolve(reader.result);
                return;
            }

            reject(new Error('Could not read the selected image.'));
        };
        reader.onerror = () => reject(new Error('Could not read the selected image.'));
        reader.readAsDataURL(file);
    });
}

async function readResponseJson(response, fallbackError) {
    const text = await response.text();
    if (!text) {
        return {};
    }

    const trimmed = text.trimStart();
    if (trimmed.startsWith('<!DOCTYPE html') || trimmed.startsWith('<html')) {
        throw new Error(fallbackError);
    }

    try {
        return JSON.parse(text);
    } catch {
        throw new Error(fallbackError);
    }
}

async function uploadImageDataUrl(session, imageDataUrl, purpose, fileName) {
    const response = await fetch(`${API_ROOT}/uploadimage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            userId: session.userId,
            jwtToken: session.jwtToken,
            imageDataUrl,
            purpose,
            fileName,
        }),
    });

    const data = await readResponseJson(response, 'Image upload failed.');
    if (!response.ok) {
        throw new Error(data.error || 'Image upload failed.');
    }

    return data;
}

async function uploadThemeImageFile(file, session, purpose, fileName) {
    validateThemeImageFile(file, 'Image');
    const dataUrl = await readFileAsDataUrl(file);
    const result = await uploadImageDataUrl(session, dataUrl, purpose, fileName);
    return result.imageUrl;
}

export function normalizeAssistantMarkdown(text) {
    return String(text || '')
        .replace(/\r\n?/g, '\n')
        .replace(/\]\s*\n\s*\(/g, '](')
        .replace(/^\s{0,3}#{1,6}\s+/gm, '');
}

export function renderInlineMarkdown(text) {
    const source = normalizeAssistantMarkdown(text);
    const nodes = [];
    const pattern = /(\*\*[^*]+\*\*|__[^_]+__|(?<!\*)\*[^*\n]+\*(?!\*)|(?<!_)_[^_\n]+_(?!_)|`[^`\n]+`|\[[^\]]+\]\s*\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<]+)/g;
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(source)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(<span key={`text-${lastIndex}`}>{source.slice(lastIndex, match.index)}</span>);
        }

        const token = match[0];
        if (
            ((token.startsWith('**') && token.endsWith('**')) ||
             (token.startsWith('__') && token.endsWith('__'))) &&
            token.length > 4
        ) {
            nodes.push(<strong key={`bold-${match.index}`}>{token.slice(2, -2)}</strong>);
        } else if (
            ((token.startsWith('*') && token.endsWith('*')) ||
             (token.startsWith('_') && token.endsWith('_'))) &&
            token.length > 2
        ) {
            nodes.push(<em key={`italic-${match.index}`}>{token.slice(1, -1)}</em>);
        } else if (token.startsWith('`') && token.endsWith('`') && token.length > 2) {
            nodes.push(<code key={`code-${match.index}`} className="ai-inline-code">{token.slice(1, -1)}</code>);
        } else {
            const linkMatch = /^\[([^\]]+)\]\s*\((https?:\/\/[^\s)]+)\)$/.exec(token);
            if (linkMatch) {
                nodes.push(
                    <a
                        key={`link-${match.index}`}
                        href={linkMatch[2]}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {linkMatch[1]}
                    </a>
                );
            } else if (/^https?:\/\/[^\s<]+$/.test(token)) {
                nodes.push(
                    <a
                        key={`url-${match.index}`}
                        href={token}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {token}
                    </a>
                );
            } else {
                nodes.push(<span key={`token-${match.index}`}>{token}</span>);
            }
        }

        lastIndex = match.index + token.length;
    }

    if (lastIndex < source.length) {
        nodes.push(<span key={`text-${lastIndex}`}>{source.slice(lastIndex)}</span>);
    }

    return nodes.length ? nodes : source;
}

export function renderAssistantMessage(text) {
    const normalizedText = normalizeAssistantMarkdown(text);
    const lines = normalizedText.split('\n');
    const blocks = [];
    let listItems = [];

    const flushList = () => {
        if (!listItems.length) {
            return;
        }

        blocks.push(
            <ul key={`list-${blocks.length}`} className="ai-message-list">
                {listItems.map((item, index) => (
                    <li key={`item-${index}`}>{renderInlineMarkdown(item)}</li>
                ))}
            </ul>
        );
        listItems = [];
    };

    lines.forEach((line, index) => {
        const trimmed = line.trim();
        const listMatch = /^([-*]|\d+\.)\s+(.*)$/.exec(trimmed);

        if (!trimmed) {
            flushList();
            return;
        }

        if (listMatch) {
            listItems.push(listMatch[2]);
            return;
        }

        flushList();
        blocks.push(
            <div key={`line-${index}`} className="ai-message-line">
                {renderInlineMarkdown(trimmed)}
            </div>
        );
    });

    flushList();

    return blocks.length > 0 ? blocks : normalizedText;
}

export function SparklesIcon() {
    return (
        <svg viewBox="0 0 24 24" className="ai-inline-icon" aria-hidden="true">
            <path d="M12 3 13.8 8.2 19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8Z" fill="currentColor" />
            <path d="M18.5 3 19.2 5 21 5.8 19.2 6.5 18.5 8.5 17.8 6.5 16 5.8 17.8 5Z" fill="currentColor" />
        </svg>
    );
}

export function SendIcon() {
    return (
        <svg viewBox="0 0 24 24" className="ai-inline-icon" aria-hidden="true">
            <path d="M3 20 21 12 3 4l3.8 7.2L15 12l-8.2.8Z" fill="currentColor" />
        </svg>
    );
}

export function LocationIcon() {
    return (
        <svg viewBox="0 0 24 24" className="ai-inline-icon" aria-hidden="true">
            <path d="M11 2h2v3h-2Z" fill="currentColor" />
            <path d="M11 19h2v3h-2Z" fill="currentColor" />
            <path d="M2 11h3v2H2Z" fill="currentColor" />
            <path d="M19 11h3v2h-3Z" fill="currentColor" />
            <path d="M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm0 2.2a2.8 2.8 0 1 1 0 5.6 2.8 2.8 0 0 1 0-5.6Z" fill="currentColor" />
        </svg>
    );
}

function App() {
    const initialSelectedDate = new Date();
    initialSelectedDate.setHours(0, 0, 0, 0);
    const [isAuthenticated, setIsAuthenticated] = useState(Boolean(localStorage.getItem('jwtToken')));
    const [leftOpen, setLeftOpen] = useState(true);
    const [rightOpen, setRightOpen] = useState(true);
    const [background, setBackground] = useState({ image: null, sceneKey: null });
    const [aiInput, setAiInput] = useState('');
    const [suggestionPreferences, setSuggestionPreferences] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [savedSuggestionKeys, setSavedSuggestionKeys] = useState([]);
    const [aiMode, setAiMode] = useState('chat');
    const [location, setLocation] = useState(null);
    const [isLocating, setIsLocating] = useState(false);
    const [locationNotice, setLocationNotice] = useState('Trying to use your current location for nearby suggestions.');
    const [messages, setMessages] = useState([
        { role: 'assistant', text: 'Ask about your day or grab event suggestions.' },
    ]);
    const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
    const [calendarModalIntent, setCalendarModalIntent] = useState(null);
    const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchMeta, setSearchMeta] = useState({
        active: false,
        loading: false,
        count: 0,
        error: '',
    });
    const [accountModalOpen, setAccountModalOpen] = useState(false);
    const [accountTab, setAccountTab] = useState('account');
    // Theme state — loaded from localStorage on mount
    const [activeTheme, setActiveTheme] = useState(() => {
        const saved = loadTheme();
        return saved || DEFAULT_THEME;
    });
    const [savedThemePacks, setSavedThemePacks] = useState(() => loadSavedThemePacks());
    // Scratch state for the theme editor (before Apply is clicked)
    const [themeDraft, setThemeDraft] = useState(null);
    const [customBgMode, setCustomBgMode] = useState('gradient'); // 'gradient' | 'universal' | 'perScene'
    const [selectedGradientStopIndex, setSelectedGradientStopIndex] = useState(0);
    const [gradientEditorOpen, setGradientEditorOpen] = useState(false);
    const [gradientEditorTarget, setGradientEditorTarget] = useState('background'); // 'background' | 'button'
    const [themeImportValue, setThemeImportValue] = useState('');
    const [themeDraftBaseline, setThemeDraftBaseline] = useState('');
    const [discardThemeDialog, setDiscardThemeDialog] = useState({
        open: false,
        nextTab: '',
        shouldCloseModal: false,
        nextTheme: null,
    });
    const [themeShareState, setThemeShareState] = useState({
        open: false,
        loading: false,
        theme: null,
        customLinkId: '',
        activeTab: 'share',
        linkAvailability: null as null | 'checking' | 'available' | 'taken' | 'invalid',
    });
    const linkAvailCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [accountSettings, setAccountSettings] = useState(null);
    const [accountDraft, setAccountDraft] = useState({
        firstName: '',
        lastName: '',
        email: '',
        pendingEmail: '',
        calendarFeedUrl: '',
        calendarFeedWebcalUrl: '',
        reminderDefaults: {
            reminderEnabled: false,
            reminderMinutesBefore: 30,
        },
    });
    const [emailDraft, setEmailDraft] = useState('');
    const [accountLoading, setAccountLoading] = useState(false);
    const [accountSaving, setAccountSaving] = useState(false);
    const [accountFeedback, setAccountFeedback] = useState('');
    const [emailFeedback, setEmailFeedback] = useState('');
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [pendingAvatarUrl, setPendingAvatarUrl] = useState(null);
    const searchInputRef = useRef(null);
    const locationRequestRef = useRef(null);
    const themeImportInputRef = useRef(null);
    const customizeSectionRef = useRef(null);
    const sharedThemeImportInFlight = useRef(false);
    const aiChatFeedRef = useRef(null);

    const currentDate = new Date();
    const verticalDateString = selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const fullDateString = selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const isSelectedToday = selectedDate.getTime() === todayDate.getTime();
    const trimmedSearchQuery = searchQuery.trim();
    const themeShareTargets = themeShareState.theme ? buildThemeShareTargets(themeShareState.theme) : null;
    const themeSharePreview = themeShareState.theme
        ? (inferThemePreview(themeShareState.theme) || themeShareState.theme.preview || 'none')
        : 'none';
    const customThemeCard = sanitizeThemePack({
        ...EMPTY_CUSTOM_THEME,
        ...(isEditableThemePack(themeDraft) ? themeDraft : {}),
        id: 'custom',
        name: 'Custom Photo Pack',
        description: 'Create and tune your own photo or gradient-based weather pack.',
        source: 'draft',
    }, EMPTY_CUSTOM_THEME);
    const featuredWeatherThemes = [DEFAULT_THEME, ...FEATURED_THEMES, customThemeCard];
    const draftPreviewBackground = themeDraft ? (inferThemePreview(themeDraft) || themeDraft.preview || 'none') : 'none';
    const themeDraftSnapshot = themeDraft ? serializeThemeDraft(themeDraft) : '';
    const themeDraftDirty = accountModalOpen && accountTab === 'themes' && Boolean(themeDraft) && themeDraftSnapshot !== themeDraftBaseline;
    const draftGradientStops = getEditableGradientStops(themeDraft?.gradient);
    const activeGradientStopIndex = Math.min(selectedGradientStopIndex, Math.max(0, draftGradientStops.length - 1));
    const activeGradientStop = draftGradientStops[activeGradientStopIndex] || draftGradientStops[0] || { color: '#60a5fa', position: 50 };
    const shareModalActions = [
        { key: 'native',     label: 'Share',     icon: ShareArrowIcon, color: '#6366f1', onClick: async () => shareThemeThroughSystem(themeShareState.theme), disabled: !themeShareState.theme?.shareUrl || typeof navigator.share !== 'function' },
        { key: 'copy-link',  label: 'Copy Link', icon: CopyIcon,       color: '#0ea5e9', onClick: () => copyThemeShareValue(themeShareState.theme?.shareUrl, 'Share link'), disabled: !themeShareState.theme?.shareUrl },
        { key: 'copy-code',  label: 'Copy Code', icon: CodeHashIcon,   color: '#8b5cf6', onClick: () => copyThemeShareValue(themeShareState.theme?.shareCode, 'Theme code'), disabled: !themeShareState.theme?.shareCode },
        { key: 'sms',        label: 'Messages',  icon: MessagesIcon,   color: '#22c55e', onClick: () => openThemeShareTarget(themeShareTargets?.sms), disabled: !themeShareState.theme?.shareUrl },
        { key: 'email',      label: 'Email',     icon: MailIcon,       color: '#f97316', onClick: () => openThemeShareTarget(themeShareTargets?.email), disabled: !themeShareState.theme?.shareUrl },
        { key: 'facebook',   label: 'Facebook',  icon: FacebookIcon,   color: '#1877f2', onClick: () => openThemeShareTarget(themeShareTargets?.facebook), disabled: !themeShareState.theme?.shareUrl },
        { key: 'whatsapp',   label: 'WhatsApp',  icon: WhatsAppIcon,   color: '#25d366', onClick: () => openThemeShareTarget(themeShareTargets?.whatsapp), disabled: !themeShareState.theme?.shareUrl },
    ].filter((item) => item.key !== 'native' || typeof navigator.share === 'function');

    const logout = () => {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('accessToken');
        setAvatarUrl(null);
        setPendingAvatarUrl(null);
        locationRequestRef.current = null;
        setLocation(null);
        setLocationNotice('Trying to use your current location for nearby suggestions.');
        setSearchQuery('');
        setSearchOpen(false);
        setIsAuthenticated(false);
    };

    const getSession = () => {
        const jwtToken = localStorage.getItem('jwtToken') || '';
        const decoded = decodeToken(jwtToken);
        if (!jwtToken || !decoded?.userId) {
            logout();
            return null;
        }

        return {
            userId: decoded.userId,
            jwtToken,
            firstName: decoded.firstName || '',
            lastName: decoded.lastName || '',
        };
    };

    const updateToken = (nextToken) => {
        if (nextToken) {
            localStorage.setItem('jwtToken', nextToken);
        }
    };

    const loadAccountSettings = async (sessionOverride = null) => {
        const session = sessionOverride || getSession();
        if (!session) {
            return;
        }

        setAccountLoading(true);
        try {
            const response = await fetch(`${API_ROOT}/getaccountsettings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    jwtToken: session.jwtToken,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Could not load account settings.');
            }

            updateToken(data.jwtToken);
            const nextSettings = data.settings || {
                firstName: session.firstName || '',
                lastName: session.lastName || '',
                email: '',
                pendingEmail: '',
                avatarUrl: '',
                calendarFeedUrl: '',
                calendarFeedWebcalUrl: '',
                customThemes: [],
                reminderDefaults: {
                    reminderEnabled: false,
                    reminderMinutesBefore: 30,
                },
            };
            setAccountSettings(nextSettings);
            setAccountDraft(nextSettings);
            setAvatarUrl(nextSettings.avatarUrl || null);
            setEmailDraft('');
            setPendingAvatarUrl(null);
            setSavedThemePacks((prev) => {
                const merged = mergeThemePacks(prev, nextSettings.customThemes || []);
                persistSavedThemePacks(merged);
                return merged;
            });
        } catch (error) {
            setAccountFeedback(error.message);
        } finally {
            setAccountLoading(false);
        }
    };

    useEffect(() => {
        const sharedThemeValue = readSharedThemeValueFromLocation();
        if (!sharedThemeValue) {
            return;
        }

        localStorage.setItem(PENDING_SHARED_THEME_STORAGE_KEY, sharedThemeValue);
        clearSharedThemeValueFromLocation();
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            const session = getSession();
            if (session) {
                loadAccountSettings(session);
            }
        } else {
            setAccountSettings(null);
            setAvatarUrl(null);
            setPendingAvatarUrl(null);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        ensureLocation();
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || sharedThemeImportInFlight.current) {
            return;
        }

        const pendingSharedTheme = localStorage.getItem(PENDING_SHARED_THEME_STORAGE_KEY) || '';
        if (!pendingSharedTheme) {
            return;
        }

        sharedThemeImportInFlight.current = true;
        importSharedTheme(pendingSharedTheme, { openDashboard: true })
            .catch((error) => {
                localStorage.removeItem(PENDING_SHARED_THEME_STORAGE_KEY);
                setAccountFeedback(error.message);
            })
            .finally(() => {
                sharedThemeImportInFlight.current = false;
            });
    }, [isAuthenticated]);

    // Apply btn-color override whenever theme changes
    useEffect(() => {
        applyBtnColorOverride(activeTheme || { btnColor: '#60a5fa' });
    }, [activeTheme]);

    useEffect(() => {
        if (!themeDraftDirty) {
            return undefined;
        }

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [themeDraftDirty]);

    useEffect(() => {
        if (!searchOpen) {
            return;
        }

        window.requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });
    }, [searchOpen]);

    useEffect(() => {
        if (aiMode !== 'chat') {
            return;
        }

        window.requestAnimationFrame(() => {
            const feed = aiChatFeedRef.current;
            if (!feed) {
                return;
            }

            feed.scrollTo({
                top: feed.scrollHeight,
                behavior: aiLoading ? 'auto' : 'smooth',
            });
        });
    }, [messages, aiLoading, aiMode]);

    const refreshCalendar = () => {
        setCalendarRefreshKey((prev) => prev + 1);
    };

    const openSearch = () => {
        setSearchOpen(true);
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchMeta({
            active: false,
            loading: false,
            count: 0,
            error: '',
        });
        searchInputRef.current?.focus();
    };

    const openAccountModal = (tab = 'account') => {
        setAccountTab(tab);
        setAccountFeedback('');
        setEmailFeedback('');
        setAccountModalOpen(true);
        if (accountSettings) {
            setAccountDraft(accountSettings);
        } else {
            loadAccountSettings();
        }
        if (tab === 'themes') {
            const nextDraft = sanitizeThemePack(cloneThemePack(activeTheme), EMPTY_CUSTOM_THEME);
            setThemeDraft(nextDraft);
            setCustomBgMode(inferThemeBackgroundMode(nextDraft));
            setThemeDraftBaseline(serializeThemeDraft(nextDraft));
        }
    };

    const syncThemeDraft = (nextTheme, options = {}) => {
        const nextDraft = sanitizeThemePack(nextTheme, EMPTY_CUSTOM_THEME);
        setThemeDraft(nextDraft);
        setCustomBgMode(inferThemeBackgroundMode(nextDraft));
        setSelectedGradientStopIndex(0);
        if (options.markClean) {
            setThemeDraftBaseline(serializeThemeDraft(nextDraft));
        }
        return nextDraft;
    };

    const closeAccountModalNow = () => {
        setAccountModalOpen(false);
        setDiscardThemeDialog({ open: false, nextTab: '', shouldCloseModal: false, nextTheme: null });
    };

    const requestAccountModalClose = () => {
        if (themeDraftDirty) {
            setDiscardThemeDialog({ open: true, nextTab: '', shouldCloseModal: true, nextTheme: null });
            return;
        }

        closeAccountModalNow();
    };

    const requestAccountTabChange = (nextTab) => {
        if (nextTab === accountTab) {
            return;
        }

        if (themeDraftDirty && accountTab === 'themes') {
            setDiscardThemeDialog({ open: true, nextTab, shouldCloseModal: false, nextTheme: null });
            return;
        }

        setAccountTab(nextTab);
        if (nextTab === 'themes') {
            syncThemeDraft(cloneThemePack(activeTheme), { markClean: true });
        }
    };

    const confirmDiscardThemeChanges = () => {
        const { nextTab, shouldCloseModal, nextTheme } = discardThemeDialog;
        setDiscardThemeDialog({ open: false, nextTab: '', shouldCloseModal: false, nextTheme: null });

        if (shouldCloseModal) {
            closeAccountModalNow();
            return;
        }

        if (nextTheme) {
            syncThemeDraft(nextTheme, { markClean: true });
            return;
        }

        if (nextTab) {
            setAccountTab(nextTab);
            if (nextTab === 'themes') {
                syncThemeDraft(cloneThemePack(activeTheme), { markClean: true });
            }
        }
    };

    const requestThemeDraftReplace = (nextTheme) => {
        if (!nextTheme || themePackMatches(themeDraft, nextTheme)) {
            return;
        }

        if (themeDraftDirty && accountTab === 'themes') {
            setDiscardThemeDialog({
                open: true,
                nextTab: '',
                shouldCloseModal: false,
                nextTheme,
            });
            return;
        }

        syncThemeDraft(nextTheme, { markClean: true });
    };

    const scrollToCustomizeSection = () => {
        customizeSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const themePackMatches = (first, second) => {
        if (!first || !second) {
            return false;
        }

        if (first.sharedThemeId || second.sharedThemeId) {
            return String(first.sharedThemeId || '') === String(second.sharedThemeId || '');
        }

        return String(first.id || '') === String(second.id || '');
    };

    const upsertSavedThemePack = (nextTheme) => {
        const normalized = sanitizeThemePack(nextTheme, EMPTY_CUSTOM_THEME);
        setSavedThemePacks((prev) => {
            const nextPacks = mergeThemePacks(prev, [normalized]);
            persistSavedThemePacks(nextPacks);
            return nextPacks;
        });
        setAccountSettings((prev) => (
            prev
                ? { ...prev, customThemes: mergeThemePacks(prev.customThemes || [], [normalized]) }
                : prev
        ));
        return normalized;
    };

    const replaceSavedThemePack = (previousTheme, nextTheme) => {
        const normalized = sanitizeThemePack(nextTheme, EMPTY_CUSTOM_THEME);
        setSavedThemePacks((prev) => {
            const filtered = prev.filter((pack) => (
                !themePackMatches(pack, previousTheme) && !themePackMatches(pack, normalized)
            ));
            const nextPacks = mergeThemePacks(filtered, [normalized]);
            persistSavedThemePacks(nextPacks);
            return nextPacks;
        });
        setAccountSettings((prev) => (
            prev
                ? {
                    ...prev,
                    customThemes: mergeThemePacks(
                        (prev.customThemes || []).filter((pack) => !themePackMatches(pack, previousTheme)),
                        [normalized],
                    ),
                }
                : prev
        ));
        return normalized;
    };

    const uploadThemeAssets = async (nextTheme, session) => {
        const uploaded = { ...nextTheme, images: { ...(nextTheme.images || {}) } };

        const uploadDataUrl = async (purpose, fileName, dataUrl) => {
            const result = await uploadImageDataUrl(session, dataUrl, purpose, fileName);
            return result.imageUrl;
        };

        const universal = String(uploaded.images?.universal || '').trim();
        if (universal.startsWith('data:')) {
            uploaded.images.universal = await uploadDataUrl(
                'theme-images',
                `${uploaded.id || 'theme'}-universal.png`,
                universal,
            );
        }

        if (Array.isArray(uploaded.galleryImages)) {
            const nextGallery = [];
            for (let index = 0; index < uploaded.galleryImages.length; index += 1) {
                const value = String(uploaded.galleryImages[index] || '').trim();
                if (!value) {
                    continue;
                }
                if (value.startsWith('data:')) {
                    nextGallery.push(await uploadDataUrl(
                        'theme-gallery',
                        `${uploaded.id || 'theme'}-gallery-${index + 1}.png`,
                        value,
                    ));
                } else {
                    nextGallery.push(value);
                }
            }
            uploaded.galleryImages = nextGallery;
        }

        const previewImage = String(uploaded.previewImage || '').trim();
        if (previewImage.startsWith('data:')) {
            uploaded.previewImage = await uploadDataUrl(
                'theme-previews',
                `${uploaded.id || 'theme'}-preview.png`,
                previewImage,
            );
        }

        const coverPhoto = String(uploaded.coverPhoto || '').trim();
        if (coverPhoto.startsWith('data:')) {
            uploaded.coverPhoto = await uploadDataUrl(
                'theme-covers',
                `${uploaded.id || 'theme'}-cover.png`,
                coverPhoto,
            );
        }

        return uploaded;
    };

    const syncThemePackToServer = async (nextTheme, options = {}) => {
        const session = getSession();
        if (!session) {
            throw new Error('Please log in again to sync this theme.');
        }

        const response = await fetch(`${API_ROOT}/upsertcustomtheme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: session.userId,
                jwtToken: session.jwtToken,
                theme: nextTheme,
                ...(options.shareSlug ? { shareSlug: options.shareSlug } : {}),
            }),
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Could not sync that theme.');
        }

        updateToken(data.jwtToken);
        return replaceSavedThemePack(nextTheme, data.theme);
    };

    const applyImportedTheme = (nextTheme, feedbackText = '') => {
        const importedTheme = upsertSavedThemePack(nextTheme);
        syncThemeDraft(importedTheme, { markClean: true });
        setActiveTheme(importedTheme);
        persistTheme(importedTheme);
        if (feedbackText) {
            setAccountFeedback(feedbackText);
        }
        return importedTheme;
    };

    const importSharedTheme = async (shareValue, options = {}) => {
        const resolvedShareValue = extractSharedThemeValue(shareValue);
        if (!resolvedShareValue) {
            throw new Error('Paste a theme code or share link first.');
        }

        const session = getSession();
        if (!session) {
            localStorage.setItem(PENDING_SHARED_THEME_STORAGE_KEY, resolvedShareValue);
            throw new Error('Log in to import that theme.');
        }

        const response = await fetch(`${API_ROOT}/importsharedtheme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: session.userId,
                jwtToken: session.jwtToken,
                shareValue: resolvedShareValue,
            }),
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || 'Could not import that theme.');
        }

        updateToken(data.jwtToken);
        const importedTheme = applyImportedTheme(
            sanitizeThemePack(data.theme, EMPTY_CUSTOM_THEME),
            `Imported "${data.theme?.name || 'theme'}" and applied it.`,
        );

        localStorage.removeItem(PENDING_SHARED_THEME_STORAGE_KEY);
        setThemeImportValue('');

        if (options.openDashboard) {
            setAccountTab('themes');
            setAccountModalOpen(true);
        }

        return importedTheme;
    };

    const openThemeShareDialog = (theme) => {
        if (!theme) {
            return;
        }

        setThemeShareState({
            open: true,
            loading: false,
            theme,
            customLinkId: theme.shareSlug || '',
            activeTab: 'share',
        });
    };

    const closeThemeShareDialog = () => {
        setThemeShareState({
            open: false,
            loading: false,
            theme: null,
            customLinkId: '',
            activeTab: 'share',
        });
    };

    const saveSharedThemeDetails = async () => {
        if (!themeShareState.theme) {
            return;
        }

        setThemeShareState((prev) => ({ ...prev, loading: true }));
        try {
            const session = getSession();
            if (!session) {
                throw new Error('Please log in again to sync this theme.');
            }
            const nextTheme = await uploadThemeAssets(themeShareState.theme, session);
            const syncedTheme = await syncThemePackToServer(nextTheme, {
                shareSlug: themeShareState.customLinkId,
            });

            if (themePackMatches(themeDraft, themeShareState.theme)) {
                syncThemeDraft(syncedTheme, { markClean: true });
            }
            if (themePackMatches(activeTheme, themeShareState.theme)) {
                setActiveTheme(syncedTheme);
                persistTheme(syncedTheme);
            }

            setThemeShareState({
                open: true,
                loading: false,
                theme: syncedTheme,
                customLinkId: syncedTheme.shareSlug || '',
                activeTab: 'share',
            });
            setAccountFeedback(`Share ready for "${syncedTheme.name}".`);
        } catch (error) {
            setThemeShareState((prev) => ({ ...prev, loading: false }));
            setAccountFeedback(error.message);
        }
    };

    const openThemeLinkEditor = () => {
        setThemeShareState((prev) => ({ ...prev, activeTab: 'edit' }));
    };

    const closeThemeLinkEditor = () => {
        setThemeShareState((prev) => ({ ...prev, activeTab: 'share', linkAvailability: null }));
    };

    const checkLinkAvailability = (slug: string, currentThemeId?: string) => {
        if (linkAvailCheckRef.current) clearTimeout(linkAvailCheckRef.current);
        const trimmed = slug.trim().toLowerCase();
        if (!trimmed) {
            setThemeShareState((prev) => ({ ...prev, linkAvailability: null }));
            return;
        }
        if (!/^[a-z0-9_-]{3,40}$/.test(trimmed)) {
            setThemeShareState((prev) => ({ ...prev, linkAvailability: 'invalid' }));
            return;
        }
        setThemeShareState((prev) => ({ ...prev, linkAvailability: 'checking' }));
        linkAvailCheckRef.current = setTimeout(async () => {
            try {
                const session = getSession();
                if (!session) return;
                const res = await fetch(`${API_ROOT}/checkthemeslug`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ slug: trimmed, excludeThemeId: currentThemeId || '', jwtToken: session.jwtToken }),
                });
                const data = await res.json();
                setThemeShareState((prev) => ({ ...prev, linkAvailability: data.available ? 'available' : 'taken' }));
            } catch {
                setThemeShareState((prev) => ({ ...prev, linkAvailability: null }));
            }
        }, 380);
    };

    const saveThemePackDraft = async () => {
        if (!themeDraft) {
            return;
        }

        const baseId = savedThemePacks.some((pack) => themePackMatches(pack, themeDraft))
            ? themeDraft.id
            : buildThemePackId(themeDraft.name);
        const savedPack = upsertSavedThemePack({
            ...themeDraft,
            id: baseId,
            source: 'user',
        });
        syncThemeDraft(savedPack, { markClean: true });
        setAccountFeedback(`Saved "${savedPack.name}" to your theme packs.`);

        try {
            const session = getSession();
            if (!session) {
                throw new Error('Please log in again to sync this theme.');
            }
            const syncedPack = await uploadThemeAssets(savedPack, session);
            const syncedTheme = await syncThemePackToServer(syncedPack);
            syncThemeDraft(syncedTheme, { markClean: true });
            if (themePackMatches(activeTheme, savedPack)) {
                setActiveTheme(syncedTheme);
                persistTheme(syncedTheme);
            }
            setAccountFeedback(`Saved "${syncedTheme.name}" to your theme packs.`);
        } catch (error) {
            setAccountFeedback(`Saved "${savedPack.name}" locally. ${error.message}`);
        }
    };

    // ── Gradient editor helpers (background or button) ───────────────────────
    const isButtonGrad = gradientEditorTarget === 'button';

    // Convert btnGradient (colors array) to the same stop format as background gradient
    const btnGradientToStops = (draft) => {
        const colors = Array.isArray(draft?.btnGradient?.colors) && draft.btnGradient.colors.length
            ? draft.btnGradient.colors
            : [draft?.btnColor || '#60a5fa'];
        return colors.map((color, i) => ({ color, position: Math.round((i / Math.max(colors.length - 1, 1)) * 100) }));
    };

    const activeEditGradient = isButtonGrad ? null : themeDraft?.gradient;
    const activeEditStops = isButtonGrad
        ? btnGradientToStops(themeDraft)
        : getEditableGradientStops(themeDraft?.gradient);
    const activeEditAngle = isButtonGrad
        ? Number(themeDraft?.btnGradient?.angle ?? 135)
        : Number(themeDraft?.gradient?.angle ?? EMPTY_CUSTOM_THEME.gradient.angle);
    const activeEditType = isButtonGrad ? 'linear' : (themeDraft?.gradient?.type || 'linear');

    const getActiveCssPreview = () => {
        if (isButtonGrad) {
            const colors = activeEditStops.map((s) => s.color);
            return colors.length >= 2
                ? `linear-gradient(${activeEditAngle}deg, ${colors.join(', ')})`
                : colors[0] || (themeDraft?.btnColor || '#60a5fa');
        }
        return buildGradientCss(themeDraft?.gradient);
    };

    const updateThemeGradient = (mutator) => {
        if (isButtonGrad) {
            setThemeDraft((prev) => {
                const currentStops = btnGradientToStops(prev);
                const nextStops = mutator(currentStops);
                const nextPrimaryColor = nextStops[0]?.color || prev?.btnColor || '#60a5fa';
                return {
                    ...prev,
                    btnColor: nextPrimaryColor,
                    btnGradient: {
                        angle: Number.isFinite(Number(prev?.btnGradient?.angle)) ? Number(prev.btnGradient.angle) : 135,
                        colors: nextStops.map((s) => s.color),
                    },
                };
            });
        } else {
            setThemeDraft((prev) => {
                const nextStops = mutator(getEditableGradientStops(prev?.gradient));
                return {
                    ...prev,
                    backgroundMode: 'gradient',
                    gradient: syncGradientStops(nextStops, prev?.gradient),
                };
            });
        }
    };

    const addGradientStop = () => {
        updateThemeGradient((stops) => {
            const baseStops = stops.length >= 1 ? stops : getEditableGradientStops(EMPTY_CUSTOM_THEME.gradient);
            const selected = baseStops[Math.min(selectedGradientStopIndex, baseStops.length - 1)] || baseStops[0];
            const position = nextGradientPosition(baseStops);
            return [...baseStops, { color: selected.color, position }];
        });
        setSelectedGradientStopIndex((prev) => Math.min(prev + 1, 8));
    };

    const removeGradientStop = () => {
        updateThemeGradient((stops) => {
            if (stops.length <= 1) return stops;
            return stops.filter((_, index) => index !== activeGradientStopIndex);
        });
        setSelectedGradientStopIndex((prev) => Math.max(0, prev - 1));
    };

    const updateGradientStopColor = (color) => {
        updateThemeGradient((stops) => stops.map((stop, index) => (
            index === activeGradientStopIndex ? { ...stop, color } : stop
        )));
    };

    const updateGradientStopPosition = (position) => {
        updateThemeGradient((stops) => stops.map((stop, index) => (
            index === activeGradientStopIndex ? { ...stop, position } : stop
        )));
    };

    const updateGradientAngle = (angle) => {
        if (isButtonGrad) {
            setThemeDraft((prev) => ({
                ...prev,
                btnGradient: {
                    colors: [...(prev?.btnGradient?.colors || [prev?.btnColor || '#60a5fa'])],
                    angle: Number(angle),
                },
            }));
        } else {
            setThemeDraft((prev) => ({
                ...prev,
                backgroundMode: 'gradient',
                gradient: { ...(prev?.gradient || EMPTY_CUSTOM_THEME.gradient), angle: Number(angle) },
            }));
        }
    };

    const updateGradientType = (type) => {
        setThemeDraft((prev) => ({
            ...prev,
            backgroundMode: 'gradient',
            gradient: { ...(prev?.gradient || EMPTY_CUSTOM_THEME.gradient), type },
        }));
    };

    // Clamp active stop index to actual stops
    const modalStops = isButtonGrad ? activeEditStops : draftGradientStops;
    const modalStopIndex = Math.min(selectedGradientStopIndex, Math.max(0, modalStops.length - 1));
    const modalActiveStop = modalStops[modalStopIndex] || modalStops[0] || { color: '#60a5fa', position: 50 };

    const gradientEditorModal = gradientEditorOpen && themeDraft && typeof document !== 'undefined'
        ? createPortal(
            <div className="theme-overlay-backdrop" onClick={() => setGradientEditorOpen(false)}>
                <div className="gx-modal" onClick={(event) => event.stopPropagation()}>
                    {/* ── Pill header ── */}
                    <div className="gx-header">
                        <div className="gx-header-titles">
                            <span className="gx-kicker">{isButtonGrad ? 'Button Color' : 'Background Gradient'}</span>
                            <span className="gx-title">{isButtonGrad ? 'Solid or gradient fill' : 'Custom Gradient'}</span>
                        </div>
                        <button type="button" className="gx-close-btn" onClick={() => setGradientEditorOpen(false)} aria-label="Close">✕</button>
                    </div>

                    {/* ── Live preview strip ── */}
                    <div className="gx-preview-strip" style={{ backgroundImage: getActiveCssPreview() }}>
                        <div className="gx-preview-shimmer" />
                    </div>

                    {/* ── Type + Angle row (background only) ── */}
                    {!isButtonGrad && (
                        <div className="gx-row gx-type-row">
                            <div className="gx-seg-group">
                                {['linear', 'radial'].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={`gx-seg${activeEditType === t ? ' active' : ''}`}
                                        onClick={() => updateGradientType(t)}
                                    >
                                        {t.charAt(0).toUpperCase() + t.slice(1)}
                                    </button>
                                ))}
                            </div>
                            <div className="gx-angle-group">
                                {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
                                    <button
                                        key={a}
                                        type="button"
                                        className={`gx-angle-chip${activeEditAngle === a ? ' active' : ''}`}
                                        onClick={() => updateGradientAngle(a)}
                                        disabled={activeEditType === 'radial'}
                                        title={`${a}°`}
                                    >
                                        {a}°
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Angle slider (button grad) ── */}
                    {isButtonGrad && modalStops.length >= 2 && (
                        <div className="gx-row">
                            <span className="gx-field-label">Angle — {activeEditAngle}°</span>
                            <input
                                className="gx-range"
                                type="range" min="0" max="360"
                                value={activeEditAngle}
                                onChange={(e) => updateGradientAngle(Number(e.target.value))}
                            />
                        </div>
                    )}

                    {/* ── Gradient rail ── */}
                    <div className="gx-rail-shell">
                        <div className="gx-rail" style={{ backgroundImage: getActiveCssPreview() }}>
                            {modalStops.map((stop, index) => (
                                <button
                                    key={`${stop.color}-${stop.position}-${index}`}
                                    type="button"
                                    className={`gx-stop-handle${index === modalStopIndex ? ' active' : ''}`}
                                    style={{ left: `${stop.position}%`, background: stop.color }}
                                    onClick={() => setSelectedGradientStopIndex(index)}
                                    aria-label={`Stop ${index + 1}`}
                                />
                            ))}
                        </div>
                        <input
                            className="gx-position-range"
                            type="range" min="0" max="100"
                            value={modalActiveStop.position}
                            onChange={(e) => updateGradientStopPosition(Number(e.target.value))}
                        />
                    </div>

                    {/* ── Stop controls ── */}
                    <div className="gx-stop-row">
                        <input
                            type="color"
                            className="gx-color-swatch"
                            value={modalActiveStop.color}
                            onChange={(e) => updateGradientStopColor(e.target.value)}
                        />
                        <input
                            type="text"
                            className="gx-color-text"
                            value={modalActiveStop.color}
                            maxLength={7}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) updateGradientStopColor(v);
                            }}
                        />
                        <input
                            type="number"
                            className="gx-position-input"
                            min="0"
                            max="100"
                            value={modalActiveStop.position}
                            onChange={(e) => updateGradientStopPosition(Number(e.target.value))}
                        />
                        <div className="gx-stop-btns">
                            <button type="button" className="gx-stop-btn" onClick={addGradientStop}>＋ Add</button>
                            <button type="button" className="gx-stop-btn" onClick={removeGradientStop} disabled={modalStops.length <= 1}>− Remove</button>
                        </div>
                    </div>
                    {isButtonGrad && (
                        <p className="theme-button-gradient-note">
                            Keep one stop for a solid button color, or add another stop to turn it into a gradient.
                        </p>
                    )}

                    {/* ── Stop pills (quick select) ── */}
                    <div className="gx-stop-pills">
                        {modalStops.map((stop, index) => (
                            <button
                                key={index}
                                type="button"
                                className={`gx-stop-pill${index === modalStopIndex ? ' active' : ''}`}
                                style={{ background: stop.color }}
                                onClick={() => setSelectedGradientStopIndex(index)}
                                aria-label={`Stop ${index + 1}: ${stop.color}`}
                            />
                        ))}
                    </div>

                    {/* ── Actions ── */}
                    <div className="gx-actions">
                        <button type="button" className="gx-btn-cancel" onClick={() => setGradientEditorOpen(false)}>Cancel</button>
                        <button type="button" className="gx-btn-ok" onClick={() => setGradientEditorOpen(false)}>Done</button>
                    </div>
                </div>
            </div>,
            document.body,
        )
        : null;

    const themeShareCreatorName = themeShareState.theme?.authorName || 'You';
    const themeShareCreatorInitials = themeShareCreatorName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('') || 'Y';

    const themeShareModal = themeShareState.open && themeShareState.theme && typeof document !== 'undefined'
        ? createPortal(
            <div className="theme-overlay-backdrop" onClick={closeThemeShareDialog}>
                <div className="theme-share-floating-modal" onClick={(event) => event.stopPropagation()}>
                    <div className="theme-share-modal-header theme-share-floating-header">
                        <div className="theme-share-header-copy">
                            <div className="account-modal-kicker">Share Theme</div>
                            <h3>{themeShareState.theme.name}</h3>
                            <div className="theme-share-creator-row">
                                {themeShareState.theme.creatorAvatarUrl || themeShareState.theme.authorAvatarUrl ? (
                                    <img
                                        src={themeShareState.theme.creatorAvatarUrl || themeShareState.theme.authorAvatarUrl}
                                        alt={themeShareCreatorName}
                                        className="theme-share-creator-avatar"
                                    />
                                ) : (
                                    <span className="theme-share-creator-avatar theme-share-creator-avatar-fallback">
                                        {themeShareCreatorInitials}
                                    </span>
                                )}
                                <p>{themeShareState.theme.creatorLabel || `Theme created by ${themeShareCreatorName}`}</p>
                            </div>
                        </div>
                        <button type="button" className="account-close-btn" onClick={closeThemeShareDialog}>
                            Done
                        </button>
                    </div>

                    <div className="theme-share-tabs" role="tablist" aria-label="Theme share tabs">
                        <button
                            type="button"
                            className={`theme-share-tab${themeShareState.activeTab === 'share' ? ' active' : ''}`}
                            onClick={closeThemeLinkEditor}
                        >
                            Share
                        </button>
                        <button
                            type="button"
                            className={`theme-share-tab${themeShareState.activeTab === 'edit' ? ' active' : ''}`}
                            onClick={openThemeLinkEditor}
                            disabled={themeShareState.theme.sharedThemeId && themeShareState.theme.isOwnedTheme !== true}
                        >
                            Edit Link
                        </button>
                    </div>

                    <div className="theme-share-modal-body theme-share-compact-body">
                        {themeShareState.activeTab === 'share' ? (
                            <>
                                <div className="theme-share-preview-panel theme-share-preview-panel-compact" style={{ backgroundImage: themeSharePreview }}>
                                    <div className="theme-share-preview-overlay">
                                        <span className="theme-share-preview-chip">{themeShareState.theme.authorLabel || 'By you'}</span>
                                        <button
                                            type="button"
                                            className="theme-preview-btn"
                                            style={resolveThemeButtonStyle(themeShareState.theme)}
                                        >
                                            Preview Theme
                                        </button>
                                    </div>
                                </div>

                                {themeShareState.theme.sharedThemeId && themeShareState.theme.isOwnedTheme !== true && (
                                    <p className="theme-share-note">This imported theme keeps the original creator name. You can share the existing link or code, but only the owner can edit the custom link.</p>
                                )}

                                <div className="theme-share-action-grid compact">
                                    <button
                                        type="button"
                                        className="theme-share-action primary"
                                        onClick={saveSharedThemeDetails}
                                        disabled={themeShareState.loading}
                                    >
                                        <span className="theme-share-action-icon">+</span>
                                        <span>{themeShareState.loading ? 'Saving...' : themeShareState.theme.shareCode ? 'Refresh Share' : 'Create Share'}</span>
                                    </button>
                                    {shareModalActions.map((action) => {
                                        const Icon = action.icon;
                                        return (
                                            <button
                                                key={action.key}
                                                type="button"
                                                className="theme-share-action"
                                                onClick={action.onClick}
                                                disabled={action.disabled}
                                                style={action.color ? { '--share-action-color': action.color, '--share-action-color-a': `${action.color}26` } as React.CSSProperties : undefined}
                                            >
                                                <span className="theme-share-action-icon" style={action.color ? { background: `${action.color}22`, border: `1px solid ${action.color}44`, color: action.color } : undefined}>
                                                    <Icon />
                                                </span>
                                                <span>{action.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="theme-share-value-stack compact">
                                    <label className="theme-share-value-card">
                                        <span>Share link</span>
                                        <input value={themeShareState.theme.shareUrl || ''} readOnly />
                                    </label>
                                    <label className="theme-share-value-card">
                                        <span>Theme code</span>
                                        <input value={themeShareState.theme.shareCode || ''} readOnly />
                                    </label>
                                </div>
                            </>
                        ) : (
                            <div className="theme-share-edit-stack">
                                <div className="theme-share-edit-card">
                                    <div>
                                        <strong>Custom link ID</strong>
                                        <p>Pick a short, memorable ID — letters, numbers, hyphens, underscores (3-40 chars).</p>
                                    </div>
                                    <div className="theme-slug-field-wrap">
                                        <label className="account-field theme-slug-label">
                                            <span>Link ID</span>
                                            <div className="theme-slug-input-row">
                                                <span className="theme-slug-prefix">theme/</span>
                                                <input
                                                    value={themeShareState.customLinkId}
                                                    onChange={(event) => {
                                                        const val = event.target.value;
                                                        setThemeShareState((prev) => ({ ...prev, customLinkId: val }));
                                                        checkLinkAvailability(val, themeShareState.theme?.sharedThemeId);
                                                    }}
                                                    placeholder="mountain-theme"
                                                    disabled={themeShareState.loading || (themeShareState.theme.sharedThemeId && themeShareState.theme.isOwnedTheme !== true)}
                                                    className="theme-slug-input"
                                                />
                                            </div>
                                        </label>
                                        {themeShareState.linkAvailability === 'checking' && (
                                            <span className="theme-slug-badge checking">Checking…</span>
                                        )}
                                        {themeShareState.linkAvailability === 'available' && (
                                            <span className="theme-slug-badge available">✓ Available</span>
                                        )}
                                        {themeShareState.linkAvailability === 'taken' && (
                                            <span className="theme-slug-badge taken">✗ Already taken</span>
                                        )}
                                        {themeShareState.linkAvailability === 'invalid' && (
                                            <span className="theme-slug-badge invalid">3-40 letters, numbers, - or _</span>
                                        )}
                                    </div>
                                </div>
                                <div className="theme-share-footer theme-share-footer-compact">
                                    <div>
                                        <strong>Export pack file</strong>
                                        <p>Download this pack as a <code>.calpp-theme.json</code> file to back up or share offline.</p>
                                    </div>
                                    <div className="theme-share-footer-actions">
                                        <button
                                            type="button"
                                            className="account-secondary-btn"
                                            onClick={() => exportThemePackDraft(themeShareState.theme)}
                                            disabled={!themeShareState.theme}
                                        >
                                            Export file
                                        </button>
                                        <button
                                            type="button"
                                            className="account-primary-btn"
                                            onClick={saveSharedThemeDetails}
                                            disabled={themeShareState.loading || themeShareState.linkAvailability === 'taken' || themeShareState.linkAvailability === 'invalid'}
                                        >
                                            {themeShareState.loading ? 'Saving…' : 'Save link ID'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            document.body,
        )
        : null;

    const discardThemeChangesModal = discardThemeDialog.open && typeof document !== 'undefined'
        ? createPortal(
            <div className="theme-overlay-backdrop" onClick={() => setDiscardThemeDialog({ open: false, nextTab: '', shouldCloseModal: false, nextTheme: null })}>
                <div className="discard-confirm-modal" onClick={(event) => event.stopPropagation()}>
                    <div className="account-modal-kicker">Unsaved changes</div>
                    <h3>Leave without saving?</h3>
                    <p>Your theme edits will be discarded if you leave this screen now.</p>
                    <div className="discard-confirm-actions">
                        <button
                            type="button"
                            className="account-secondary-btn"
                            onClick={() => setDiscardThemeDialog({ open: false, nextTab: '', shouldCloseModal: false, nextTheme: null })}
                        >
                            Keep editing
                        </button>
                        <button
                            type="button"
                            className="account-primary-btn"
                            onClick={confirmDiscardThemeChanges}
                        >
                            Discard changes
                        </button>
                    </div>
                </div>
            </div>,
            document.body,
        )
        : null;

    const exportThemePackDraft = (theme = themeDraft) => {
        if (!theme) {
            return;
        }

        const pack = sanitizeThemePack({
            ...theme,
            source: 'shared',
        }, EMPTY_CUSTOM_THEME);
        const blob = new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${pack.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'theme-pack'}.calpp-theme.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const importThemePackFile = async (file) => {
        if (!file) {
            return;
        }

        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const importedPack = sanitizeThemePack({
                ...parsed,
                id: buildThemePackId(parsed?.name || 'imported-pack'),
                source: 'user',
            }, EMPTY_CUSTOM_THEME);
            applyImportedTheme(importedPack, `Imported "${importedPack.name}" and applied it.`);
        } catch {
            setAccountFeedback('Could not import that theme pack.');
        }
    };

    const deleteThemePack = async (packToDelete) => {
        if (!packToDelete) {
            return;
        }

        const session = getSession();
        if (packToDelete.sharedThemeId && session) {
            try {
                const response = await fetch(`${API_ROOT}/deletecustomtheme`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: session.userId,
                        jwtToken: session.jwtToken,
                        sharedThemeId: packToDelete.sharedThemeId,
                    }),
                });

                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.error || 'Could not remove that theme.');
                }

                updateToken(data.jwtToken);
            } catch (error) {
                setAccountFeedback(error.message);
                return;
            }
        }

        setSavedThemePacks((prev) => {
            const nextPacks = prev.filter((pack) => !themePackMatches(pack, packToDelete));
            persistSavedThemePacks(nextPacks);
            return nextPacks;
        });
        setAccountSettings((prev) => (
            prev
                ? {
                    ...prev,
                    customThemes: (prev.customThemes || []).filter((pack) => !themePackMatches(pack, packToDelete)),
                }
                : prev
        ));

        if (themePackMatches(themeDraft, packToDelete)) {
            syncThemeDraft(EMPTY_CUSTOM_THEME, { markClean: true });
        }
        if (themePackMatches(activeTheme, packToDelete)) {
            setActiveTheme(DEFAULT_THEME);
            persistTheme(DEFAULT_THEME);
        }
        if (themePackMatches(themeShareState.theme, packToDelete)) {
            closeThemeShareDialog();
        }

        setAccountFeedback('Saved theme pack removed.');
    };

    const copyThemeShareValue = async (value, label) => {
        if (!value) {
            setAccountFeedback(`${label} is not ready yet.`);
            return;
        }

        try {
            await navigator.clipboard.writeText(value);
            setAccountFeedback(`${label} copied.`);
        } catch {
            setAccountFeedback(value);
        }
    };

    const openThemeShareTarget = (targetUrl) => {
        if (!targetUrl) {
            return;
        }

        if (targetUrl.startsWith('mailto:') || targetUrl.startsWith('sms:')) {
            window.location.href = targetUrl;
            return;
        }

        window.open(targetUrl, '_blank', 'noopener,noreferrer');
    };

    const shareThemeThroughSystem = async (theme) => {
        if (!theme?.shareUrl || typeof navigator.share !== 'function') {
            return;
        }

        const targets = buildThemeShareTargets(theme);
        await navigator.share({
            title: `Calendar++ theme: ${theme.name}`,
            text: targets.shareText,
            url: theme.shareUrl,
        });
    };

    const openCalendarModal = (kind) => {
        setCalendarModalIntent({
            kind,
            date: new Date().toISOString(),
            key: Date.now(),
        });
    };

    const saveAccountSettings = async () => {
        const session = getSession();
        if (!session) {
            return;
        }

        setAccountSaving(true);
        setAccountFeedback('');
        try {
            let avatarUrlValue = null;
            if (pendingAvatarUrl !== null) {
                if (pendingAvatarUrl === 'REMOVED') {
                    avatarUrlValue = '';
                } else {
                    const avatarUpload = await uploadImageDataUrl(
                        session,
                        pendingAvatarUrl,
                        'avatars',
                        'avatar.png',
                    );
                    avatarUrlValue = avatarUpload.imageUrl;
                }
            }
            const requestBody = {
                userId: session.userId,
                jwtToken: session.jwtToken,
                firstName: accountDraft.firstName,
                lastName: accountDraft.lastName,
                reminderEnabled: accountDraft.reminderDefaults?.reminderEnabled === true,
                reminderMinutesBefore: Number(accountDraft.reminderDefaults?.reminderMinutesBefore || 30),
                ...(pendingAvatarUrl !== null ? { avatarUrl: avatarUrlValue } : {}),
            };
            const response = await fetch(`${API_ROOT}/saveaccountsettings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Could not save settings.');
            }

            updateToken(data.jwtToken);
            const nextSettings = data.settings || accountDraft;
            setAccountSettings(nextSettings);
            setAccountDraft(nextSettings);
            setAvatarUrl(nextSettings.avatarUrl || null);
            setPendingAvatarUrl(null);
            setAccountFeedback('Settings saved.');
        } catch (error) {
            setAccountFeedback(error.message);
        } finally {
            setAccountSaving(false);
        }
    };

    const requestEmailChange = async () => {
        const session = getSession();
        if (!session || !emailDraft.trim()) {
            return;
        }

        setAccountSaving(true);
        setEmailFeedback('');
        try {
            const response = await fetch(`${API_ROOT}/requestemailchange`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    jwtToken: session.jwtToken,
                    nextEmail: emailDraft.trim(),
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Could not start email change.');
            }

            updateToken(data.jwtToken);
            setEmailFeedback('Verification sent to the new email address.');
            await loadAccountSettings();
            setEmailDraft('');
        } catch (error) {
            setEmailFeedback(error.message);
        } finally {
            setAccountSaving(false);
        }
    };

    const exportCalendar = async () => {
        const session = getSession();
        if (!session) {
            return;
        }

        try {
            const response = await fetch(`${API_ROOT}/exportcalendar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    jwtToken: session.jwtToken,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Could not export calendar.');
            }

            updateToken(data.jwtToken);
            const blob = new Blob([data.ics || ''], { type: 'text/calendar;charset=utf-8' });
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = data.filename || 'calendar-plus-plus.ics';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(downloadUrl);
            setAccountFeedback('Calendar exported.');
        } catch (error) {
            setAccountFeedback(error.message);
        }
    };

    const copyCalendarFeed = async (useWebcal = false) => {
        const nextLink = useWebcal
            ? accountSettings?.calendarFeedWebcalUrl
            : accountSettings?.calendarFeedUrl;

        if (!nextLink) {
            setAccountFeedback('Calendar feed link is not ready yet.');
            return;
        }

        try {
            await navigator.clipboard.writeText(nextLink);
            setAccountFeedback(useWebcal ? 'Subscription link copied.' : 'Feed URL copied.');
        } catch {
            setAccountFeedback(nextLink);
        }
    };

    const regenerateCalendarFeed = async () => {
        const session = getSession();
        if (!session) {
            return;
        }

        setAccountSaving(true);
        setAccountFeedback('');
        try {
            const response = await fetch(`${API_ROOT}/regeneratecalendarfeed`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    jwtToken: session.jwtToken,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Could not regenerate calendar feed.');
            }

            updateToken(data.jwtToken);
            setAccountSettings(data.settings);
            setAccountDraft(data.settings);
            setAccountFeedback('Subscription link regenerated. Old links no longer work.');
        } catch (error) {
            setAccountFeedback(error.message);
        } finally {
            setAccountSaving(false);
        }
    };

    const ensureLocation = async () => {
        if (location && !location.isFallback) {
            return location;
        }

        if (locationRequestRef.current) {
            return locationRequestRef.current;
        }

        const locationRequest = (async () => {
            setIsLocating(true);
            setLocationNotice('Checking location...');

            try {
                const coords = await requestWeatherLocation();

                if (!coords.isFallback) {
                    setLocation(coords);
                    setLocationNotice('Nearby suggestions are using your current location.');
                    return coords;
                }

                setLocationNotice('Location is blocked, set to UCF');
                return coords;
            } finally {
                locationRequestRef.current = null;
                setIsLocating(false);
            }
        })();

        locationRequestRef.current = locationRequest;
        return locationRequest;
    };

    const loadSuggestions = async () => {
        const session = getSession();
        if (!session) return;

        setAiLoading(true);
        setAiMode('suggestions');
        try {
            const localNow = new Date();
            const coords = await ensureLocation();
            const response = await fetch(`${API_ROOT}/suggestevents`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    jwtToken: session.jwtToken,
                    date: localNow.toISOString(),
                    localNow: localNow.toISOString(),
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    utcOffsetMinutes: -localNow.getTimezoneOffset(),
                    preferences: suggestionPreferences.trim(),
                    latitude: coords?.latitude,
                    longitude: coords?.longitude,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Could not load suggestions.');
            }

            updateToken(data.jwtToken);
            setSuggestions(normalizeSuggestions(data.suggestions));
        } catch (error) {
            setMessages((prev) => [...prev, { role: 'assistant', text: error.message }]);
        } finally {
            setAiLoading(false);
        }
    };

    const updateStreamingAssistantMessage = (updater) => {
        setMessages((prev) => {
            const updated = [...prev];
            const lastIndex = updated.length - 1;
            const previous = updated[lastIndex];
            const seed = previous?.role === 'assistant'
                ? previous
                : { role: 'assistant', text: '', status: '' };
            const nextMessage = updater(seed);

            if (previous?.role === 'assistant') {
                updated[lastIndex] = nextMessage;
                return updated;
            }

            return [...updated, nextMessage];
        });
    };

    const sendChat = async () => {
        const trimmed = aiInput.trim();
        const session = getSession();
        if (!trimmed || !session || aiLoading) return;

        const nextMessages = [...messages, { role: 'user', text: trimmed }];
        setAiMode('chat');
        setMessages([...nextMessages, { role: 'assistant', text: '', status: 'Thinking' }]);
        setAiInput('');
        setAiLoading(true);

        try {
            const localNow = new Date();
            const coords = await ensureLocation();
            const response = await fetch(`${API_ROOT}/chatstream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    jwtToken: session.jwtToken,
                    messages: nextMessages.map((message) => ({
                        role: message.role,
                        content: message.text,
                    })),
                    localNow: localNow.toISOString(),
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    utcOffsetMinutes: -localNow.getTimezoneOffset(),
                    latitude: coords?.latitude,
                    longitude: coords?.longitude,
                }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Chat failed.');
            }

            if (!response.body) {
                throw new Error('Streaming is not available.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine) {
                        continue;
                    }

                    let payload;
                    try {
                        payload = JSON.parse(trimmedLine);
                    } catch {
                        continue;
                    }

                    if (payload.type === 'delta' && payload.delta) {
                        updateStreamingAssistantMessage((previous) => ({
                            ...previous,
                            text: `${previous.text}${payload.delta}`,
                            status: '',
                        }));
                    } else if (payload.type === 'status' && payload.status) {
                        updateStreamingAssistantMessage((previous) => ({
                            ...previous,
                            status: payload.status,
                        }));
                        await waitForNextPaint();
                    } else if (payload.type === 'done') {
                        updateToken(payload.jwtToken);
                        updateStreamingAssistantMessage((previous) => ({
                            ...previous,
                            status: '',
                        }));
                        if (payload.calendarChanged) {
                            refreshCalendar();
                        }
                    } else if (payload.type === 'error') {
                        throw new Error(payload.error || 'Streaming failed.');
                    }
                }
            }

            const finalLine = buffer.trim();
            if (finalLine) {
                let payload;
                try {
                    payload = JSON.parse(finalLine);
                } catch {
                    payload = null;
                }

                if (payload?.type === 'done') {
                    updateToken(payload.jwtToken);
                } else if (payload?.type === 'error') {
                    throw new Error(payload.error || 'Streaming failed.');
                }
            }
        } catch (error) {
            setMessages((prev) => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                const previous = updated[lastIndex];
                if (previous?.role === 'assistant' && previous.text === '') {
                    updated[lastIndex] = { role: 'assistant', text: error.message };
                    return updated;
                }

                return [...updated, { role: 'assistant', text: error.message }];
            });
        } finally {
            setAiLoading(false);
        }
    };

    const saveSuggestion = async (suggestion) => {
        const session = getSession();
        const key = suggestionKey(suggestion);
        if (!session || savedSuggestionKeys.includes(key) || aiLoading) {
            return;
        }

        setAiLoading(true);
        try {
            const startDate = dateWithSuggestedTime(currentDate, suggestion.suggestedTime || '');
            const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
            const response = await fetch(`${API_ROOT}/savecalendar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    jwtToken: session.jwtToken,
                    title: suggestion.title,
                    description: suggestion.description,
                    dueDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    source: 'manual',
                    isCompleted: false,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Could not save suggestion.');
            }

            updateToken(data.jwtToken);
            setSavedSuggestionKeys((prev) => [...prev, key]);
            refreshCalendar();
        } catch (error) {
            setMessages((prev) => [...prev, { role: 'assistant', text: error.message }]);
            setAiMode('chat');
        } finally {
            setAiLoading(false);
        }
    };

    const currentSession = isAuthenticated ? getSession() : null;
    const profileFirstName = accountSettings?.firstName || currentSession?.firstName || 'John';
    const profileLastName = accountSettings?.lastName || currentSession?.lastName || 'Doe';
    const profileInitials = `${profileFirstName.charAt(0)}${profileLastName.charAt(0)}`.toUpperCase();
    const sidebarAvatarUrl = pendingAvatarUrl !== null
        ? (pendingAvatarUrl === 'REMOVED' ? '' : pendingAvatarUrl)
        : (avatarUrl || accountSettings?.avatarUrl || '');
    const [profileAvatarFailed, setProfileAvatarFailed] = useState(false);

    useEffect(() => {
        setProfileAvatarFailed(false);
    }, [sidebarAvatarUrl]);

    // Compute the effective background: theme override takes precedence over weather
    const effectiveBackground = toCssBackgroundImage(resolveEffectiveBackground(activeTheme, background));

    // Render the password reset screen directly when the reset route is active.
    const isResetRoute = window.location.pathname === '/resetpassword' &&
                         new URLSearchParams(window.location.search).has('token');
    if (isResetRoute) {
        return <ResetPasswordPage />;
    }

    return (
        <>
            {isAuthenticated && currentSession ? (
                <>
                    <div
                        className="main-layout"
                        style={{
                            '--bg-img': effectiveBackground || 'none',
                            backgroundSize: inferImageFit(activeTheme),
                        }}
                    >
                        <div className={`sidebar left-sidebar ${leftOpen ? 'open' : 'closed'}`}>
                        <button className="toggle-btn right-align" onClick={() => setLeftOpen(!leftOpen)}>
                            <img src={leftOpen ? leftCloseIcon : leftOpenIcon} alt="Toggle Left" />
                        </button>

                        {leftOpen ? (
                            <div className="sidebar-content">
                                <div style={{ marginBottom: '20px' }}>
                                    <h2 style={{ margin: '0 0 5px 0' }}>{isSelectedToday ? 'Today' : 'Plan'}</h2>
                                    <p style={{ margin: 0, color: 'var(--btn-color)', fontWeight: 'bold' }}>{fullDateString}</p>
                                </div>

                                <nav style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <button className="nav-item" onClick={() => openCalendarModal('plan')}><span className="nav-icon">Plan</span></button>
                                    <button className="nav-item" onClick={() => openCalendarModal('event')}><span className="nav-icon">Event</span></button>
                                    <button className="nav-item" onClick={() => openCalendarModal('task')}><span className="nav-icon">Task</span></button>
                                    <hr style={{ border: '0', borderTop: '1px solid #2c2c3e', margin: '10px 0' }} />
                                    <button className="nav-item" onClick={() => openCalendarModal('import')}><span className="nav-icon">Import</span></button>
                                    <button className="nav-item" onClick={() => openAccountModal('settings')}><span className="nav-icon">Settings</span></button>
                                </nav>

                                <button
                                    type="button"
                                    className="profile-summary-button"
                                    onClick={() => openAccountModal('account')}
                                >
                                    <div className="profile-summary-avatar">
                                        {sidebarAvatarUrl && !profileAvatarFailed ? (
                                            <img
                                                src={sidebarAvatarUrl}
                                                alt={`${profileFirstName} ${profileLastName}`}
                                                className="profile-summary-avatar-img"
                                                onError={() => setProfileAvatarFailed(true)}
                                            />
                                        ) : (
                                            profileInitials
                                        )}
                                    </div>
                                    <div className="profile-summary-copy">
                                        <span className="profile-summary-name">{`${profileFirstName} ${profileLastName}`}</span>
                                        <span className="profile-summary-email">{accountSettings?.email || ''}</span>
                                    </div>
                                </button>
                                <div className="logout-container">
                                    <button onClick={logout} className="logout-btn">
                                        Logout
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="vertical-date">
                                {verticalDateString}
                            </div>
                        )}
                    </div>

                    <div className="center-content">
                        <div className="calendar-wrapper">
                            <Calendar
                                singleMonth={false}
                                setBackground={setBackground}
                                session={currentSession}
                                apiRoot={API_ROOT}
                                onSessionRefresh={updateToken}
                                refreshKey={calendarRefreshKey}
                                modalIntent={calendarModalIntent}
                                reminderDefaults={accountSettings?.reminderDefaults}
                                onSelectedDateChange={setSelectedDate}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                onSearchMetaChange={setSearchMeta}
                            />
                        </div>
                    </div>

                    <div className={`sidebar right-sidebar ${rightOpen ? 'open' : 'closed'}`}>
                        <button className="toggle-btn left-align" onClick={() => setRightOpen(!rightOpen)}>
                            <img src={rightOpen ? rightCloseIcon : rightOpenIcon} alt="Toggle Right" />
                        </button>
                        {rightOpen && (
                            <div className="sidebar-content ai-panel">
                                <div className="ai-panel-header">
                                    <h2>AI Assistant</h2>
                                </div>
                                <div className="ai-hero-card">
                                    <h3>Schedule ideas with context</h3>
                                    <div className="ai-location-row">
                                        <span className="ai-location-text">{locationNotice}</span>
                                        <button
                                            className="ai-icon-btn"
                                            type="button"
                                            onClick={ensureLocation}
                                            disabled={isLocating}
                                            aria-label="Refresh location"
                                        >
                                            {isLocating ? '...' : <LocationIcon />}
                                        </button>
                                    </div>
                                    <textarea
                                        className="ai-input ai-preferences"
                                        placeholder="Suggestion preferences"
                                        value={suggestionPreferences}
                                        onChange={(event) => setSuggestionPreferences(event.target.value)}
                                    />
                                    <button
                                        className="ai-send-btn ai-suggest-btn"
                                        onClick={loadSuggestions}
                                        disabled={aiLoading}
                                    >
                                        <SparklesIcon />
                                        {aiLoading && aiMode === 'suggestions'
                                            ? 'Loading...'
                                            : `Suggest events for ${currentDate.getMonth() + 1}/${currentDate.getDate()}/${currentDate.getFullYear()}`}
                                    </button>
                                </div>

                                {aiMode === 'suggestions' ? (
                                    <>
                                        <div className="ai-main-shell suggestions-mode">
                                            <div className="ai-section ai-suggestions active">
                                                <div className="ai-section-header">
                                                    <h3>Suggestions</h3>
                                                    <span>{suggestions.length} ready</span>
                                                </div>
                                                <div className="ai-suggestion-list">
                                                    {suggestions.length > 0 ? (
                                                        suggestions.map((suggestion, index) => {
                                                            const key = suggestionKey(suggestion);
                                                            const isSaved = savedSuggestionKeys.includes(key);

                                                            return (
                                                                <div
                                                                    key={`${key}-${index}`}
                                                                    className="ai-suggestion-card"
                                                                >
                                                                    <div className="ai-suggestion-copy">
                                                                        <div className="ai-suggestion-time">
                                                                            {suggestion.suggestedTime || 'No time'}
                                                                        </div>
                                                                        <div className="ai-suggestion-title">{suggestion.title}</div>
                                                                        <div className="ai-suggestion-description">{suggestion.description}</div>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className={`ai-add-btn ${isSaved ? 'saved' : ''}`}
                                                                        onClick={() => saveSuggestion(suggestion)}
                                                                        disabled={isSaved || aiLoading}
                                                                        aria-label={isSaved ? 'Already added' : 'Add to calendar'}
                                                                    >
                                                                        {isSaved ? '\u2713' : '+'}
                                                                    </button>
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <div className="ai-empty-state">
                                                            Use the button above and I&apos;ll fill this panel with ideas for today.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="ai-composer">
                                            <textarea
                                                className="ai-input ai-message-input"
                                                placeholder="Message the assistant..."
                                                value={aiInput}
                                                onChange={(event) => setAiInput(event.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' && !event.shiftKey) {
                                                        event.preventDefault();
                                                        sendChat();
                                                    }
                                                }}
                                            />
                                            <button className="ai-send-btn ai-composer-send" onClick={sendChat} disabled={aiLoading}>
                                                {aiLoading && aiMode === 'chat' ? '...' : <SendIcon />}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="ai-chat-shell">
                                        <div className="ai-main-shell chat-mode">
                                            <div className="ai-section ai-chat active">
                                                <div className="ai-chat-feed" ref={aiChatFeedRef}>
                                                    {messages.map((message, index) => (
                                                        <div
                                                            key={`${message.role}-${index}`}
                                                            className={`ai-message ${message.role === 'user' ? 'user' : 'assistant'}${message.role === 'assistant' && !message.text && message.status ? ' status-loading' : ''}`}
                                                        >
                                                            {message.role === 'assistant'
                                                                ? (
                                                                    message.text
                                                                        ? renderAssistantMessage(message.text)
                                                                        : message.status
                                                                            ? <span className="ai-status-text">{displayAssistantStatus(message.status)}</span>
                                                                            : ''
                                                                )
                                                                : message.text}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="ai-composer ai-composer-inline">
                                            <textarea
                                                className="ai-input ai-message-input"
                                                placeholder="Message the assistant..."
                                                value={aiInput}
                                                onChange={(event) => setAiInput(event.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' && !event.shiftKey) {
                                                        event.preventDefault();
                                                        sendChat();
                                                    }
                                                }}
                                            />
                                            <button className="ai-send-btn ai-composer-send" onClick={sendChat} disabled={aiLoading}>
                                                {aiLoading && aiMode === 'chat' ? '...' : <SendIcon />}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        </div>
                    </div>
                    {accountModalOpen && (
                        <div className="account-modal-overlay" onClick={requestAccountModalClose}>
                            <div className="account-modal" onClick={(event) => event.stopPropagation()}>
                            <div className="account-modal-header">
                                <div>
                                    <div className="account-modal-kicker">Account</div>
                                    <h2>Account & Settings</h2>
                                </div>
                                <button type="button" className="account-close-btn" onClick={requestAccountModalClose}>
                                    Close
                                </button>
                            </div>

                            <div className="account-modal-tabs">
                                <button
                                    type="button"
                                    className={`account-tab-btn ${accountTab === 'account' ? 'active' : ''}`}
                                    onClick={() => requestAccountTabChange('account')}
                                >
                                    Account
                                </button>
                                <button
                                    type="button"
                                    className={`account-tab-btn ${accountTab === 'settings' ? 'active' : ''}`}
                                    onClick={() => requestAccountTabChange('settings')}
                                >
                                    Settings
                                </button>
                                <button
                                    type="button"
                                    className={`account-tab-btn ${accountTab === 'themes' ? 'active' : ''}`}
                                    onClick={() => requestAccountTabChange('themes')}
                                >
                                    Themes
                                </button>
                            </div>

                            <div className="account-modal-body">
                                <div className="account-hero">
                                    <div className="account-avatar-shell">
                                        {pendingAvatarUrl !== null && pendingAvatarUrl !== 'REMOVED' ? (
                                            <img src={pendingAvatarUrl} alt="Profile" className="account-avatar account-avatar-img" />
                                        ) : (pendingAvatarUrl !== 'REMOVED' && avatarUrl) ? (
                                            <img src={avatarUrl} alt="Profile" className="account-avatar account-avatar-img" />
                                        ) : (
                                            <div className="account-avatar">{profileInitials}</div>
                                        )}
                                        <label className="account-avatar-btn" style={{ cursor: 'pointer' }}>
                                            Upload picture
                                            <input
                                                type="file"
                                                accept={AVATAR_ACCEPT}
                                                style={{ display: 'none' }}
                                                onChange={async (event) => {
                                                    const file = event.target.files?.[0];
                                                    event.target.value = '';
                                                    if (!file) return;

                                                    try {
                                                        setAccountFeedback('');
                                                        validateAvatarFile(file);
                                                        const nextAvatarUrl = await readFileAsDataUrl(file);
                                                        setPendingAvatarUrl(nextAvatarUrl);
                                                    } catch (error) {
                                                        setAccountFeedback(error.message);
                                                    }
                                                }}
                                            />
                                        </label>
                                        {(avatarUrl || pendingAvatarUrl) && pendingAvatarUrl !== 'REMOVED' && (
                                            <button type="button" className="account-avatar-remove-btn" onClick={() => { setPendingAvatarUrl('REMOVED'); }}>
                                                Remove picture
                                            </button>
                                        )}
                                    </div>
                                    <div className="account-hero-copy">
                                        <h3>{`${profileFirstName} ${profileLastName}`}</h3>
                                        <p>{accountSettings?.email || 'Loading email...'}</p>
                                        {accountSettings?.pendingEmail && (
                                            <div className="account-pending-badge">
                                                Pending email: {accountSettings.pendingEmail}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {accountLoading ? (
                                    <div className="account-feedback-panel">Loading account details...</div>
                                ) : (
                                    <>
                                        {accountTab === 'account' ? (
                                            <div className="account-section-stack">
                                                <div className="account-section-card">
                                                    <h3>Profile</h3>
                                                    <div className="account-field-row">
                                                        <label className="account-field">
                                                            <span>First name</span>
                                                            <input
                                                                value={accountDraft.firstName || ''}
                                                                onChange={(event) => setAccountDraft((prev) => ({ ...prev, firstName: event.target.value }))}
                                                            />
                                                        </label>
                                                        <label className="account-field">
                                                            <span>Last name</span>
                                                            <input
                                                                value={accountDraft.lastName || ''}
                                                                onChange={(event) => setAccountDraft((prev) => ({ ...prev, lastName: event.target.value }))}
                                                            />
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="account-section-card">
                                                    <h3>Email</h3>
                                                    <label className="account-field">
                                                        <span>Current email</span>
                                                        <input value={accountSettings?.email || ''} disabled />
                                                    </label>
                                                    <label className="account-field">
                                                        <span>New email</span>
                                                        <input
                                                            value={emailDraft}
                                                            onChange={(event) => setEmailDraft(event.target.value)}
                                                            placeholder="name@example.com"
                                                        />
                                                    </label>
                                                    <div className="account-inline-actions">
                                                        <button type="button" className="account-primary-btn" onClick={requestEmailChange} disabled={accountSaving || !emailDraft.trim()}>
                                                            {accountSaving ? 'Sending...' : 'Verify new email'}
                                                        </button>
                                                    </div>
                                                    {emailFeedback && <div className="account-feedback-panel">{emailFeedback}</div>}
                                                </div>
                                            </div>
                                        ) : accountTab === 'themes' ? (
                                            /* ── THEMES TAB ─────────────────────────────────────────── */
                                            <div className="account-section-stack">
                                                <div className="account-section-card">
                                                    <div className="theme-library-header">
                                                        <div>
                                                            <h3>Featured Weather Photo Themes</h3>
                                                            <p className="account-section-copy">Photo-based theme packs for weather-aware backgrounds.</p>
                                                        </div>
                                                    </div>
                                                    <div className="featured-theme-grid">
                                                        {featuredWeatherThemes.map((theme) => {
                                                            const isSelected = themeDraft?.id === theme.id;
                                                            const previewBackground = inferThemePreview(theme) || theme.preview;
                                                            return (
                                                                <button
                                                                    key={theme.id}
                                                                    type="button"
                                                                    className={`featured-theme-card${isSelected ? ' selected' : ''}`}
                                                                    onClick={() => requestThemeDraftReplace(theme)}
                                                                >
                                                                    <div
                                                                        className="featured-theme-media"
                                                                        style={{ backgroundImage: previewBackground }}
                                                                    >
                                                                        <span className="featured-theme-chip">
                                                                            {theme.id === 'default' ? 'Default' : theme.id === 'custom' ? 'Custom' : 'Featured'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="featured-theme-copy">
                                                                        <span className="featured-theme-name">{theme.name}</span>
                                                                        <span className="featured-theme-desc">{theme.description}</span>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    {isEditableThemePack(themeDraft) && (
                                                        <div className="theme-customize-shortcut">
                                                            <div className="theme-customize-shortcut-copy">
                                                                <strong>Make this pack yours</strong>
                                                                <span>Jump to the controls for button colors, gradients, and background tuning on {themeDraft?.name || 'your current pack'}.</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="account-primary-btn theme-customize-shortcut-btn"
                                                                onClick={scrollToCustomizeSection}
                                                            >
                                                                Customize current pack
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="account-section-card theme-saved-section">
                                                    {/* ── Section header ── */}
                                                    <div className="theme-section-masthead">
                                                        <div className="theme-section-masthead-copy">
                                                            <h3>Your Saved &amp; Shared Themes</h3>
                                                            <p className="account-section-copy">Import, save, and manage your packs.</p>
                                                        </div>
                                                        <div className="theme-library-actions">
                                                            <button
                                                                type="button"
                                                                className="account-secondary-btn"
                                                                onClick={() => themeImportInputRef.current?.click()}
                                                            >
                                                                Import file
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="theme-share-pill-btn"
                                                                onClick={() => openThemeShareDialog(themeDraft)}
                                                                disabled={!themeDraft}
                                                                title="Share current theme"
                                                                aria-label="Share theme"
                                                            >
                                                                <img src={shareIcon} alt="" style={{ width: 16, height: 16, filter: 'invert(1)', opacity: 0.9 }} />
                                                                Share
                                                            </button>
                                                            <input
                                                                ref={themeImportInputRef}
                                                                type="file"
                                                                accept=".json,.calpp-theme.json,application/json"
                                                                style={{ display: 'none' }}
                                                                onChange={async (event) => {
                                                                    const file = event.target.files?.[0];
                                                                    event.target.value = '';
                                                                    await importThemePackFile(file);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* ── Import by code ── */}
                                                    <div className="theme-import-pill-row">
                                                        <input
                                                            className="theme-import-pill-input"
                                                            value={themeImportValue}
                                                            onChange={(event) => setThemeImportValue(event.target.value)}
                                                            placeholder="Paste a share link or 6-digit code…"
                                                        />
                                                        <button
                                                            type="button"
                                                            className="account-primary-btn"
                                                            onClick={async () => {
                                                                try {
                                                                    await importSharedTheme(themeImportValue);
                                                                } catch (error) {
                                                                    setAccountFeedback(error.message);
                                                                }
                                                            }}
                                                            disabled={!themeImportValue.trim()}
                                                        >
                                                            Import &amp; Apply
                                                        </button>
                                                    </div>

                                                    {/* ── Saved packs grid ── */}
                                                    {savedThemePacks.length > 0 ? (
                                                        <div className="saved-theme-grid">
                                                            {savedThemePacks.map((pack) => {
                                                                const isSelected = themePackMatches(themeDraft, pack);
                                                                const previewBackground = inferThemePreview(pack) || pack.preview;
                                                                return (
                                                                    <div
                                                                        key={pack.sharedThemeId || pack.id}
                                                                        className={`saved-theme-card${isSelected ? ' selected' : ''}`}
                                                                        onClick={() => requestThemeDraftReplace(pack)}
                                                                        onKeyDown={(event) => {
                                                                            if (event.key === 'Enter' || event.key === ' ') {
                                                                                event.preventDefault();
                                                                                requestThemeDraftReplace(pack);
                                                                            }
                                                                        }}
                                                                        role="button"
                                                                        tabIndex={0}
                                                                    >
                                                                        <div
                                                                            className="saved-theme-swatch"
                                                                            style={{ background: previewBackground }}
                                                                        >
                                                                            {isSelected && <span className="saved-theme-active-badge">Active</span>}
                                                                        </div>
                                                                        <div className="saved-theme-copy">
                                                                            <span className="saved-theme-name">{pack.name}</span>
                                                                            <span className="saved-theme-meta">{pack.authorLabel || 'By you'}</span>
                                                                            <span className="saved-theme-desc">{pack.description || 'Custom theme pack'}</span>
                                                                        </div>
                                                                        <div className="saved-theme-actions">
                                                                            <button
                                                                                type="button"
                                                                                className="saved-theme-share-btn"
                                                                                title="Share theme"
                                                                                aria-label={`Share ${pack.name}`}
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    openThemeShareDialog(pack);
                                                                                }}
                                                                            >
                                                                                <img src={shareIcon} alt="" style={{ width: 14, height: 14, filter: 'invert(1)', opacity: 0.85 }} />
                                                                                Share
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="saved-theme-delete-btn"
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    deleteThemePack(pack);
                                                                                }}
                                                                            >
                                                                                Delete
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="theme-empty-well">
                                                            <span className="theme-empty-icon">🎨</span>
                                                            <p>No saved or imported themes yet.</p>
                                                            <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Create a custom pack above or paste a share code.</p>
                                                        </div>
                                                    )}
                                                </div>

                                                {isEditableThemePack(themeDraft) && (
                                                    <div className="account-section-card">
                                                        <div className="theme-preview-card">
                                                            <div className="theme-library-header">
                                                                <div>
                                                                    <h3>Preview</h3>
                                                                    <p className="account-section-copy">Live preview of the current pack while you edit it.</p>
                                                                </div>
                                                                <div className="theme-library-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="account-primary-btn"
                                                                        onClick={saveThemePackDraft}
                                                                        disabled={!themeDraft}
                                                                    >
                                                                        Save Pack
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className="theme-preview-strip"
                                                                style={{ backgroundImage: draftPreviewBackground }}
                                                            >
                                                                <div className="theme-preview-overlay">
                                                                    <button
                                                                        type="button"
                                                                        className="theme-preview-btn"
                                                                        style={resolveThemeButtonStyle(themeDraft)}
                                                                    >
                                                                        Button Preview
                                                                    </button>
                                                                    <span className="theme-preview-label">{themeDraft?.name || 'Custom Photo Pack'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {isEditableThemePack(themeDraft) && (
                                                    <div ref={customizeSectionRef} className="account-section-card theme-customize-section">
                                                        <div className="theme-section-masthead">
                                                            <div className="theme-section-masthead-copy">
                                                                <h3>Customize</h3>
                                                                <p className="account-section-copy">Colors, gradients, and backgrounds for <strong>{themeDraft?.name || 'your pack'}</strong>.</p>
                                                            </div>
                                                        </div>

                                                        {/* ── Pack Identity ─────────────────────────── */}
                                                        <div className="theme-identity-section">
                                                            <div className="theme-identity-cover-wrap">
                                                                <div
                                                                    className="theme-identity-cover"
                                                                    style={{
                                                                        backgroundImage: (themeDraft as any).coverPhoto
                                                                            ? `url(${(themeDraft as any).coverPhoto})`
                                                                            : draftPreviewBackground,
                                                                    }}
                                                                >
                                                                    <label className="theme-identity-cover-upload-btn" title="Change cover photo">
                                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                                                                        <input
                                                                            type="file"
                                                                            accept={THEME_IMAGE_ACCEPT}
                                                                            style={{ display: 'none' }}
                                                                            onChange={async (e) => {
                                                                                const file = e.target.files?.[0];
                                                                                e.target.value = '';
                                                                                if (!file) return;
                                                                                try {
                                                                                    const session = getSession();
                                                                                    if (!session) throw new Error('Please log in again to upload.');
                                                                                    const url = await uploadThemeImageFile(file, session, 'theme-covers', `${themeDraft?.id || 'theme'}-cover.${file.name.split('.').pop() || 'jpg'}`);
                                                                                    setThemeDraft((prev) => ({ ...prev, coverPhoto: url } as any));
                                                                                } catch (error) { setAccountFeedback((error as any).message); }
                                                                            }}
                                                                        />
                                                                    </label>
                                                                    {(themeDraft as any).coverPhoto && (
                                                                        <button
                                                                            type="button"
                                                                            className="theme-identity-cover-clear-btn"
                                                                            title="Remove cover photo"
                                                                            onClick={() => setThemeDraft((prev) => ({ ...prev, coverPhoto: undefined } as any))}
                                                                        >×</button>
                                                                    )}
                                                                </div>
                                                                <p className="theme-identity-cover-hint">Cover photo</p>
                                                            </div>
                                                            <div className="theme-identity-fields">
                                                                <label className="account-field">
                                                                    <span>Pack name</span>
                                                                    <input
                                                                        type="text"
                                                                        value={themeDraft.name || ''}
                                                                        maxLength={48}
                                                                        placeholder="My Awesome Pack"
                                                                        onChange={(e) => setThemeDraft((prev) => ({ ...prev, name: e.target.value }))}
                                                                    />
                                                                </label>
                                                                <label className="account-field">
                                                                    <span>Short description</span>
                                                                    <textarea
                                                                        className="theme-identity-desc"
                                                                        value={themeDraft.description || ''}
                                                                        maxLength={120}
                                                                        rows={2}
                                                                        placeholder="A quick note about this pack…"
                                                                        onChange={(e) => setThemeDraft((prev) => ({ ...prev, description: e.target.value }))}
                                                                    />
                                                                    <span className="theme-identity-charcount">{(themeDraft.description || '').length}/120</span>
                                                                </label>
                                                            </div>
                                                        </div>

                                                        {/* Button color picker */}
                                                        <div className="theme-custom-row theme-color-inline-grid">
                                                            <label className="theme-color-label">
                                                                <span>Button color</span>
                                                                <div className="theme-color-row">
                                                                    <input
                                                                        type="color"
                                                                        className="theme-color-input"
                                                                        value={themeDraft.btnColor || '#60a5fa'}
                                                                        onChange={(e) => setThemeDraft((prev) => {
                                                                            const nextColor = e.target.value;
                                                                            const nextGradientColors = Array.isArray(prev?.btnGradient?.colors) ? [...prev.btnGradient.colors] : [];
                                                                            if (nextGradientColors.length <= 1) {
                                                                                return {
                                                                                    ...prev,
                                                                                    btnColor: nextColor,
                                                                                    btnGradient: nextGradientColors.length
                                                                                        ? { ...(prev?.btnGradient || {}), colors: [nextColor] }
                                                                                        : prev?.btnGradient,
                                                                                };
                                                                            }

                                                                            return { ...prev, btnColor: nextColor };
                                                                        })}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        className="theme-color-text"
                                                                        value={themeDraft.btnColor || '#60a5fa'}
                                                                        maxLength={7}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                                                                                setThemeDraft((prev) => {
                                                                                    const nextGradientColors = Array.isArray(prev?.btnGradient?.colors) ? [...prev.btnGradient.colors] : [];
                                                                                    if (nextGradientColors.length <= 1) {
                                                                                        return {
                                                                                            ...prev,
                                                                                            btnColor: v,
                                                                                            btnGradient: nextGradientColors.length
                                                                                                ? { ...(prev?.btnGradient || {}), colors: [v] }
                                                                                                : prev?.btnGradient,
                                                                                        };
                                                                                    }

                                                                                    return { ...prev, btnColor: v };
                                                                                });
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            </label>
                                                            <label className="theme-color-label">
                                                                <span>Button text color</span>
                                                                <div className="theme-color-row">
                                                                    <input
                                                                        type="color"
                                                                        className="theme-color-input"
                                                                        value={themeDraft.btnTextColor || getContrastTextColor(themeDraft.btnColor || '#60a5fa')}
                                                                        onChange={(e) => setThemeDraft((prev) => ({ ...prev, btnTextColor: e.target.value }))}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        className="theme-color-text"
                                                                        value={themeDraft.btnTextColor || getContrastTextColor(themeDraft.btnColor || '#60a5fa')}
                                                                        maxLength={7}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                                                                                setThemeDraft((prev) => ({ ...prev, btnTextColor: v }));
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            </label>
                                                        </div>

                                                        <div className="theme-button-style-row">
                                                            <div className="theme-button-style-copy">
                                                                <strong>Button fill</strong>
                                                                <span>{(themeDraft.btnGradient?.colors?.length || 1) >= 2 ? 'Gradient is active on preview buttons.' : 'Solid color is active. Add another stop for a gradient.'}</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="account-secondary-btn"
                                                                onClick={() => { setGradientEditorTarget('button'); setGradientEditorOpen(true); }}
                                                            >
                                                                Edit button fill
                                                            </button>
                                                            {(themeDraft.btnGradient?.colors?.length || 0) >= 2 && (
                                                                <button
                                                                    type="button"
                                                                    className="account-secondary-btn"
                                                                    onClick={() => setThemeDraft((prev) => ({ ...prev, btnGradient: undefined }))}
                                                                >
                                                                    Use solid color
                                                                </button>
                                                            )}
                                                        </div>

                                                        {/* Background image mode toggle */}
                                                        <div className="theme-bg-mode-row">
                                                            <button
                                                                type="button"
                                                                className={`account-tab-btn${customBgMode === 'gradient' ? ' active' : ''}`}
                                                                style={{ fontSize: '12px', padding: '6px 14px' }}
                                                                onClick={() => {
                                                                    setCustomBgMode('gradient');
                                                                    setThemeDraft((prev) => ({
                                                                        ...prev,
                                                                        backgroundMode: 'gradient',
                                                                    }));
                                                                }}
                                                            >
                                                                Gradient Background
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`account-tab-btn${customBgMode === 'universal' ? ' active' : ''}`}
                                                                style={{ fontSize: '12px', padding: '6px 14px' }}
                                                                onClick={() => {
                                                                    setCustomBgMode('universal');
                                                                    setThemeDraft((prev) => ({
                                                                        ...prev,
                                                                        backgroundMode: 'universal',
                                                                    }));
                                                                }}
                                                            >
                                                                Single background
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`account-tab-btn${customBgMode === 'perScene' ? ' active' : ''}`}
                                                                style={{ fontSize: '12px', padding: '6px 14px' }}
                                                                onClick={() => {
                                                                    setCustomBgMode('perScene');
                                                                    setThemeDraft((prev) => ({
                                                                        ...prev,
                                                                        backgroundMode: 'perScene',
                                                                    }));
                                                                }}
                                                            >
                                                                Per-weather scenes
                                                            </button>
                                                        </div>

                                                        {customBgMode === 'gradient' ? (
                                                            <div className="theme-gradient-summary-card">
                                                                <div
                                                                    className="theme-gradient-summary-preview"
                                                                    style={{ backgroundImage: buildGradientCss(themeDraft.gradient) }}
                                                                />
                                                                <div className="theme-gradient-summary-copy">
                                                                    <strong>Custom Gradient</strong>
                                                                    <span>{`${themeDraft.gradient?.type || 'linear'} / ${draftGradientStops.length} stop${draftGradientStops.length === 1 ? '' : 's'}`}</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="account-primary-btn"
                                                                    onClick={() => { setGradientEditorTarget('background'); setGradientEditorOpen(true); }}
                                                                >
                                                                    Edit Gradient
                                                                </button>
                                                            </div>
                                                        ) : customBgMode === 'universal' ? (
                                                            <div className="theme-bg-upload-row">
                                                                <label className="theme-bg-upload-label">
                                                                    <div className="theme-bg-thumb" style={{
                                                                        backgroundImage: themeDraft.images?.universal ? `url(${themeDraft.images.universal})` : 'none',
                                                                    }}>
                                                                        {!themeDraft.images?.universal && <span className="theme-bg-placeholder">No image</span>}
                                                                    </div>
                                                                    <div className="theme-bg-info">
                                                                        <span className="theme-bg-title">Background image</span>
                                                                        <span className="theme-bg-hint">Used for all weather conditions</span>
                                                                        <span className="theme-bg-hint">Most image types supported, &lt; 6 MB</span>
                                                                    </div>
                                                                    <input
                                                                        type="file"
                                                                        accept={THEME_IMAGE_ACCEPT}
                                                                        style={{ display: 'none' }}
                                                                    onChange={async (e) => {
                                                                            const file = e.target.files?.[0];
                                                                            e.target.value = '';
                                                                            if (!file) return;
                                                                            try {
                                                                                const session = getSession();
                                                                                if (!session) {
                                                                                    throw new Error('Please log in again to upload this image.');
                                                                                }
                                                                                const url = await uploadThemeImageFile(file, session, 'theme-images', `${themeDraft?.id || 'theme'}-universal.${file.name.split('.').pop() || 'png'}`);
                                                                                setThemeDraft((prev) => ({ ...prev, images: { ...prev.images, universal: url } }));
                                                                            } catch (error) {
                                                                                setAccountFeedback(error.message);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="account-secondary-btn"
                                                                        style={{ pointerEvents: 'none' }}
                                                                    >
                                                                        Choose image
                                                                    </button>
                                                                </label>
                                                                {themeDraft.images?.universal && (
                                                                    <button
                                                                        type="button"
                                                                        className="account-secondary-btn"
                                                                        onClick={() => setThemeDraft((prev) => ({ ...prev, images: {} }))}
                                                                    >
                                                                        Remove image
                                                                    </button>
                                                                )}
                                                                {/* Image fit selector */}
                                                                {themeDraft.images?.universal && (
                                                                    <label className="account-field" style={{ marginTop: 8 }}>
                                                                        <span>Image fit</span>
                                                                        <select
                                                                            value={themeDraft.imageFit || 'cover'}
                                                                            onChange={(e) => setThemeDraft((prev) => ({ ...prev, imageFit: e.target.value }))}
                                                                        >
                                                                            <option value="cover">Cover (fill &amp; crop)</option>
                                                                            <option value="contain">Contain (show full image)</option>
                                                                            <option value="center">Center (no scaling)</option>
                                                                        </select>
                                                                    </label>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="theme-scene-grid">
                                                                {WEATHER_SLOTS.map(({ key, label }) => {
                                                                    const img = themeDraft.images?.[key];
                                                                    return (
                                                                        <label key={key} className="theme-scene-cell">
                                                                            <div className="theme-scene-thumb" style={{
                                                                                backgroundImage: img ? `url(${img})` : 'none',
                                                                            }}>
                                                                                {!img && <span className="theme-bg-placeholder">+</span>}
                                                                            </div>
                                                                            <span className="theme-scene-label">{label}</span>
                                                                            <input
                                                                                type="file"
                                                                                accept={THEME_IMAGE_ACCEPT}
                                                                                style={{ display: 'none' }}
                                                                                onChange={async (e) => {
                                                                                    const file = e.target.files?.[0];
                                                                                    e.target.value = '';
                                                                                    if (!file) return;
                                                                                    try {
                                                                                        const session = getSession();
                                                                                        if (!session) {
                                                                                            throw new Error('Please log in again to upload this image.');
                                                                                        }
                                                                                        const ext = file.name.split('.').pop() || 'png';
                                                                                        const url = await uploadThemeImageFile(file, session, 'theme-gallery', `${themeDraft?.id || 'theme'}-${key}.${ext}`);
                                                                                        setThemeDraft((prev) => ({ ...prev, images: { ...prev.images, [key]: url } }));
                                                                                    } catch (error) {
                                                                                        setAccountFeedback(error.message);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                            </div>
                                        ) : (
                                            <div className="account-section-stack">
                                                <div className="account-section-card">
                                                    <h3>Reminder defaults</h3>
                                                    <label className="account-check-row">
                                                        <input
                                                            type="checkbox"
                                                            checked={accountDraft.reminderDefaults?.reminderEnabled === true}
                                                            onChange={(event) => setAccountDraft((prev) => ({
                                                                ...prev,
                                                                reminderDefaults: {
                                                                    ...prev.reminderDefaults,
                                                                    reminderEnabled: event.target.checked,
                                                                },
                                                            }))}
                                                        />
                                                        <span>Enable reminders by default for new items</span>
                                                    </label>
                                                    <label className="account-field">
                                                        <span>Default reminder timing</span>
                                                        <select
                                                            value={Number(accountDraft.reminderDefaults?.reminderMinutesBefore || 30)}
                                                            disabled={accountDraft.reminderDefaults?.reminderEnabled !== true}
                                                            onChange={(event) => setAccountDraft((prev) => ({
                                                                ...prev,
                                                                reminderDefaults: {
                                                                    ...prev.reminderDefaults,
                                                                    reminderMinutesBefore: Number(event.target.value),
                                                                },
                                                            }))}
                                                        >
                                                            {REMINDER_OPTIONS.map((option) => (
                                                                <option key={option.value} value={option.value}>{option.label}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                </div>

                                                <div className="account-section-card">
                                                    <h3>Calendar data</h3>
                                                    <p className="account-section-copy">
                                                        Import and connected calendar tools stay in the calendar import modal. You can also export your current calendar as an iCal file here.
                                                    </p>
                                                    <label className="account-field">
                                                        <span>Subscription feed URL</span>
                                                        <input value={accountSettings?.calendarFeedUrl || ''} readOnly />
                                                    </label>
                                                    <div className="account-inline-actions account-inline-actions-wrap">
                                                        <button type="button" className="account-secondary-btn" onClick={() => copyCalendarFeed(false)}>
                                                            Copy feed URL
                                                        </button>
                                                        <button type="button" className="account-secondary-btn" onClick={() => copyCalendarFeed(true)}>
                                                            Copy webcal link
                                                        </button>
                                                        <button type="button" className="account-secondary-btn" onClick={regenerateCalendarFeed} disabled={accountSaving}>
                                                            Regenerate link
                                                        </button>
                                                    </div>
                                                    <div className="account-inline-actions">
                                                        <button type="button" className="account-primary-btn" onClick={exportCalendar}>
                                                            Export iCal
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {accountFeedback && <div className="account-feedback-panel">{accountFeedback}</div>}
                                    </>
                                )}
                            </div>

                            <div className="account-modal-actions">
                                <button type="button" className="account-secondary-btn" onClick={requestAccountModalClose}>
                                    Close
                                </button>
                                {accountTab === 'themes' ? (
                                    <>
                                        <button
                                            type="button"
                                            className="account-secondary-btn"
                                            onClick={() => {
                                                const reset = DEFAULT_THEME;
                                                setActiveTheme(reset);
                                                syncThemeDraft(reset, { markClean: true });
                                                persistTheme(reset);
                                            }}
                                        >
                                            Reset to default
                                        </button>
                                        <button
                                            type="button"
                                            className="account-primary-btn"
                                            disabled={!themeDraft}
                                            onClick={() => {
                                                if (!themeDraft) return;
                                                const appliedTheme = syncThemeDraft(themeDraft, { markClean: true });
                                                setActiveTheme(appliedTheme);
                                                persistTheme(appliedTheme);
                                                setAccountFeedback('Theme applied.');
                                            }}
                                        >
                                            Apply theme
                                        </button>
                                    </>
                                ) : (
                                    <button type="button" className="account-primary-btn" onClick={saveAccountSettings} disabled={accountSaving || accountLoading}>
                                        {accountSaving ? 'Saving...' : 'Save changes'}
                                    </button>
                                )}
                            </div>
                            </div>
                        </div>
                    )}
                    {discardThemeChangesModal}
                    {gradientEditorModal}
                    {themeShareModal}
                </>
            ) : (
                <Login setIsAuthenticated={setIsAuthenticated} />
            )}
        </>
    );
}

export default App;
